import React, { useState, useRef, useEffect, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Paper,
  Typography,
  Button,
  IconButton,
  Divider,
  Avatar,
  Popover,
  CircularProgress,
  SvgIcon,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Slider,
  TextField
} from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatColorText as ColorIcon,
  EmojiEmotions as EmojiIcon,
  Image as ImageIcon,
  Keyboard as KeyboardIcon,
  Palette as PaletteIcon,
  Brush as BrushIcon,
  LineWeight as LineWeightIcon,
  FormatColorFill as FillIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Send as SendIcon,
  Check as CheckIcon,
  ViewStream as ViewDefaultIcon,
  ViewHeadline as ViewCompactIcon,
  ChatBubbleOutlined as ViewBubbleIcon,
  KeyboardArrowDown as ArrowDownIcon,
  QuickreplyOutlined as QuickReplyIcon
} from '@mui/icons-material';
import EmojiPicker, { Theme as EmojiTheme } from 'emoji-picker-react';
import { renderRichText, resolveUsernameColor, wireToPlainText } from '../utils/richText';
import MessageEditor from './composer/MessageEditor';
import { IMAGE_ACCEPT, prepareImageFile } from './composer/imageValidation';
import QuickRepliesPopover, {
  QUICK_REPLY_MAX,
  QUICK_REPLY_MAX_LEN,
} from './QuickRepliesPopover';
import PhotoViewer from './PhotoViewer';
import socketService from '../services/socketService';
import { resolveUploadUrl, resolveAvatarUrl, getTenantInfoFromURL } from '../services/api';
import { BUILTIN_AVATAR_PALETTE, normalizeBuiltinColor } from '../services/builtinAvatars';
import AvatarFallback from './AvatarFallback';
import JesterHat from './JesterHat';
import { fontSizeRem } from '../utils/displayPrefs';
import { formatRelativeTime, formatClockTime } from '../utils/formatTime';
import { LONG_PRESS_MS } from './UserContextMenu';
import Taskbar from '../windowing/Taskbar';
import { useWindowState } from '../windowing/WindowManagerContext';

// Inline image thumbnail: constrain the LARGER of width/height to this many px;
// the image keeps its aspect ratio. Clicking opens the full-size original in a
// new tab.
const IMAGE_THUMB_MAX_PX = 240;

// Human-readable byte size for file-transfer offers/progress ("1.2 MB").
const formatBytes = (n) => {
  if (!n || n < 1024) return `${n || 0} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
};

// Message-density / layout options for the chat header switcher. This is a
// per-viewer READING preference for the whole transcript (Gmail/Slack/Discord
// scope it this way), so it lives in the chat-section header next to "Chat",
// not in the message composer toolbar.
const VIEW_MODE_OPTIONS = [
  { value: 'default', label: 'Default', Icon: ViewDefaultIcon },
  { value: 'compact', label: 'Compact', Icon: ViewCompactIcon },
  { value: 'bubble', label: 'Bubble', Icon: ViewBubbleIcon },
];

// Swatch palette for the text-color picker — the classic 36-color HTML5-chat
// grid (6 rows x 6). Hex values are 6-digit so they round-trip through the
// [#hex|...] markup parsed in renderRichText.
// Drawing canvas CSS height. Width stretches to the composer (the client's
// browser width), which is what determines the sent image's resolution.
const DRAW_CANVAS_HEIGHT = 300;

// Brush size bounds (px) for the width slider. The slider runs 1..MAX, integers
// only; drawStroke holds the radius used when drawing.
const MAX_BRUSH_SIZE = 60;

// Sentinel for a transparent canvas background (no fill is baked into the PNG).
const TRANSPARENT_BG = 'transparent';

// CSS checkerboard, used to visualize a transparent background.
const CHECKERBOARD = {
  backgroundColor: '#2a2f3a',
  backgroundImage:
    'linear-gradient(45deg, #444b58 25%, transparent 25%), linear-gradient(-45deg, #444b58 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #444b58 75%), linear-gradient(-45deg, transparent 75%, #444b58 75%)',
  backgroundSize: '12px 12px',
  backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
};

// Brush-color swatches: the same palette as the avatar/username color picker
// (AvatarPickerDialog), flattened row-major from the column-major legacy grid so
// each on-screen column keeps its hue-family grouping (grays, reds, oranges, …).
const BRUSH_SWATCHES = (() => {
  const out = [];
  for (let row = 0; row < 5; row += 1) {
    for (let col = 0; col < BUILTIN_AVATAR_PALETTE.length; col += 1) {
      out.push(BUILTIN_AVATAR_PALETTE[col][row]);
    }
  }
  return out;
})();

// A toolbar glyph with a small color swatch chip in the corner. The icon keeps
// its normal (theme) color so it stays visible even when the chosen color is
// black/dark against the dark toolbar; the chip carries the actual color, with
// a hairline border so pure white and pure black both read.
const ChipIcon = ({ icon, color }) => (
  <span style={{ position: 'relative', display: 'inline-flex' }}>
    {icon}
    <span
      style={{
        position: 'absolute', right: -2, bottom: -2,
        width: 8, height: 8, borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
        ...(color === TRANSPARENT_BG ? CHECKERBOARD : { backgroundColor: color }),
      }}
    />
  </span>
);

// Eraser glyph (Material has no eraser icon). Sized to match fontSize="small".
const EraserIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M16.24 3.56l4.2 4.2a1.5 1.5 0 010 2.12L11.66 18.66H7.5l-3.66-3.66a1.5 1.5 0 010-2.12l10.28-10.28a1.5 1.5 0 012.12 0zM6 20h14v2H6z" />
  </SvgIcon>
);


const COLOR_LIST = [
  '#000000', '#993300', '#333300', '#000080', '#333399', '#333333',
  '#800000', '#FF6600', '#808000', '#008000', '#008080', '#0000FF',
  '#666699', '#808080', '#FF0000', '#FF9900', '#99CC00', '#339966',
  '#33CCCC', '#3366FF', '#800080', '#999999', '#FF00FF', '#FFCC00',
  '#FFFF00', '#00FF00', '#00FFFF', '#00CCFF', '#993366', '#C0C0C0',
  '#FF99CC', '#FFCC99', '#FFFF99', '#CCFFFF', '#99CCFF', '#FFFFFF',
];

const useStyles = makeStyles()((theme) => ({
  // The chat column carries the (theme) surface. In sesh it's a faint
  // translucent wash (mockup `.chat`) so the body glow bleeds through; the
  // header/messages/composer below are transparent so they don't stack opacity
  // — they share this one surface, like the mockup.
  chatPanel: {
    position: 'relative', // anchors the full-cover drag-and-drop overlay
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.palette.custom.chatBg,
  },
  header: {
    padding: theme.spacing(0.5, 1, 0.5, 2),
    backgroundColor: 'transparent',
    borderBottom: `1px solid ${theme.palette.custom.line}`,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    minHeight: 36,
  },
  headerText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  viewModeButton: {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.custom.hoverOverlay,
    },
  },
  // Clear-screen button. The wrapping <span> carries marginLeft:auto so this
  // button + the layout button form a right-aligned group (a Tooltip needs a
  // non-disabled wrapper to fire while the IconButton is disabled).
  clearButton: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
    '& .MuiIconButton-root': {
      color: 'inherit',
      '&:hover': {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.custom.hoverOverlay,
      },
    },
  },
  viewMenuItem: {
    fontSize: '0.875rem',
    '& .MuiListItemIcon-root': {
      minWidth: 36,
    },
  },
  viewCheckIcon: {
    marginLeft: theme.spacing(1.5),
    color: theme.palette.primary.main,
  },
  // Positioned wrapper around the scrollable list so the "Jump to latest" pill
  // can float over the messages without scrolling away with them.
  messagesViewport: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  messagesContainer: {
    flex: 1,
    minHeight: 0, // critical: lets the list scroll instead of growing the panel
    padding: theme.spacing(1),
    backgroundColor: 'transparent', // share the chat column surface (mockup .chat-log)
    overflowY: 'auto',
    // display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  // Floating "N new messages — jump to latest" pill, centered at the bottom of
  // the viewport while the user is scroll-locked above new arrivals.
  jumpToLatest: {
    position: 'absolute',
    bottom: theme.spacing(1.5),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1.5),
    border: 'none',
    borderRadius: 99,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.custom.accentContrastText,
    fontFamily: 'inherit',
    fontSize: '0.75rem',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
    '&:hover': { filter: 'brightness(1.08)' },
    '& svg': { fontSize: '1rem' },
  },
  // Inline "N new messages" divider marking where the user scroll-locked.
  unreadDivider: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    margin: theme.spacing(1, 0.5),
    color: theme.palette.primary.main,
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    gap: theme.spacing(1),
    '&::before, &::after': {
      content: '""',
      flex: 1,
      height: 1,
      backgroundColor: theme.palette.primary.main,
      opacity: 0.4,
    },
  },
  message: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    position: 'relative',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
    },
    // 'off' timestamp format: hide the inline time, but reveal it when the row
    // is hovered (desktop). Keyed off a data attribute on the timestamp span so
    // it works regardless of generated class names. (Touch devices get the
    // reveal via the row's title tooltip instead.)
    '& [data-ts-hideable="true"]': {
      visibility: 'hidden',
    },
    '&:hover [data-ts-hideable="true"]': {
      visibility: 'visible',
    },
    // Hover-reveal for the "Save as quick reply" action on own messages.
    // Targeted via a literal extra class (like [data-ts-hideable]) so the
    // row's :hover can reach it without tss-react $ref gymnastics. Opacity
    // (not display) keeps it keyboard-focusable.
    '&:hover .msg-save-action': {
      opacity: 1,
    },
  },
  // "Save as quick reply" hover action on own messages — hidden until the row
  // is hovered (see the .msg-save-action rule inside `message` above).
  msgHoverAction: {
    opacity: 0,
    padding: 2,
    marginLeft: 4,
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    transition: 'opacity 120ms',
    '&:focus-visible': {
      opacity: 1,
    },
  },
  // Avatar sized in `em` so it scales with the viewport font size (32px at the
  // 100% base of 0.875rem ≈ 2.286em). The fallback initial inside also uses em.
  userAvatar: {
    width: '2.286em',
    height: '2.286em',
    flexShrink: 0,
    cursor: 'pointer',
    // backgroundColor: theme.palette.primary.main,
    color: theme.palette.custom.accentContrastText,
    fontSize: '1em',
    fontWeight: 600,
  },
  // Avatars always paint their own pixels (photo, builtin, or the builtin
  // default fallback) — drop the colored fill behind them. '&&' doubles the
  // class specificity so this deterministically beats MUI's
  // .MuiAvatar-colorDefault regardless of emotion injection order (sx
  // overrides proved order-dependent). Shared with MentionPlugin, which
  // reuses our classes.
  avatarPhoto: {
    '&&': { background: 'transparent' },
  },
  userBadge: {
    position: 'absolute',
    top: theme.spacing(0.5),
    left: 24,
    width: 16,
    height: 16,
    '& img': {
      width: '100%',
      height: '100%',
    },
  },
  messageContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  // Compact mode row: the small dot avatar should sit centered against the
  // single content line rather than top-aligned (the default for multi-line
  // default rows). Overrides message's alignItems: 'flex-start'.
  messageCompact: {
    alignItems: 'start',
  },
  // Compact mode: username, text and timestamp sit on one row to fit more lines.
  // baseline (not the invalid 'top') aligns the bold username, the message text,
  // and the timestamp on a shared text baseline despite their different sizes.
  compactContent: {
    flexDirection: 'row',
    alignItems: 'start',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  // Compact mode shrinks the avatar to a small inline dot. Keep fontSize at 1em
  // (the row base) so the em width/height resolve against the viewport size, not
  // a shrunk local font; 16px at the 100% base ≈ 1.143em.
  avatarCompact: {
    width: '1.143em',
    height: '1.143em',
  },
  // Bubble mode: a chat bubble around the message text.
  bubble: {
    alignItems: 'flex-start',
  },
  bubbleText: {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: 12,
    padding: theme.spacing(0.75, 1.25),
    display: 'inline-block',
    maxWidth: '85%',
  },
  // A follow-up bubble in a same-user run: no avatar, indented to align under
  // the first bubble's text.
  bubbleFollowUp: {
    paddingTop: 1,
    paddingBottom: 1,
    marginLeft: 40, // avatar width (32) + gap (8)
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  usernameLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: 2,
  },
  username: {
    fontSize: '0.857em', // 0.75/0.875 of the viewport base — scales with font size
    fontWeight: 'bold',
    color: theme.palette.custom.leaf, // default username accent (per-theme green)
  },
  usernameSpecial: {
    color: theme.palette.primary.main, // special users → theme primary accent
  },
  timestamp: {
    fontSize: '0.714em', // 0.625/0.875 of the viewport base — scales with font size
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
    marginLeft: 'auto',
  },
  messageText: {
    fontSize: '1em', // = the viewport base font size (driven by the container)
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap', // preserve newlines/spaces from Shift+Enter multiline
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
  },
  // Inline image thumbnail. The larger dimension is capped at
  // IMAGE_THUMB_MAX_PX; aspect ratio is preserved. The whole thing is a link
  // (opens full size in a new tab), so give it a pointer + subtle affordance.
  imageLink: {
    display: 'inline-block',
    // As a flex item inside `messageContent` (a stretch flex column) an
    // inline-block still stretches to the full row width, making the empty
    // space beside the thumbnail a click target for the lightbox. Pin the
    // wrapper to its content so only the image itself is clickable.
    alignSelf: 'flex-start',
    width: 'fit-content',
    marginTop: 2,
    lineHeight: 0, // kill the inline-image descender gap
    borderRadius: 6,
    overflow: 'hidden',
    // border: `1px solid ${theme.palette.custom.line}`,
    maxWidth: '100%',
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  imageThumb: {
    display: 'block',
    maxWidth: IMAGE_THUMB_MAX_PX,
    maxHeight: IMAGE_THUMB_MAX_PX,
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
  },
  // emoji-picker-react renders its own surface; the Popover Paper just hosts
  // it. Strip Paper chrome and retheme the picker's CSS variables to match the
  // active app theme (works for both Midnight and Sesh — both are dark).
  emojiPopoverPaper: {
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    '& .EmojiPickerReact': {
      '--epr-bg-color': theme.palette.custom.panel,
      '--epr-category-label-bg-color': theme.palette.custom.panel,
      '--epr-hover-bg-color': theme.palette.custom.accentWash,
      '--epr-focus-bg-color': theme.palette.custom.accentWash,
      '--epr-highlight-color': theme.palette.primary.main,
      '--epr-search-border-color': theme.palette.primary.main,
      '--epr-text-color': theme.palette.text.primary,
      '--epr-search-input-bg-color': theme.palette.custom.panelRaised,
      '--epr-picker-border-color': theme.palette.custom.line,
      '--epr-category-icon-active-color': theme.palette.primary.main,
      border: 'none',
    },
  },
  // Quick Replies toolbar popover — dark themed Paper hosting the popover body.
  quickReplyPopoverPaper: {
    backgroundColor: theme.palette.custom.panel,
    backgroundImage: 'none',
    border: `1px solid ${theme.palette.custom.line}`,
  },
  colorPicker: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.custom.panel,
    maxWidth: 200,
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  colorSwatch: {
    width: 20,
    height: 20,
    padding: 0,
    borderRadius: 3,
    border: '1px solid rgba(255, 255, 255, 0.25)',
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.15)',
      borderColor: '#ffffff',
    },
    '&.active': {
      borderColor: '#ffffff',
      boxShadow: '0 0 0 2px #ffffff',
    },
  },
  // Brush color popover: avatar-picker palette (10-wide swatch grid) plus a
  // custom-hex entry row underneath.
  brushColorPicker: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.custom.panel,
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  brushSwatchGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(10, 1fr)',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  brushHexRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  brushHexLabel: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  brushHexInput: {
    width: 92,
    '& input': {
      fontFamily: 'monospace',
      textTransform: 'uppercase',
    },
  },
  // Floating Discord-style format bar over a mouse selection. Compact pill that
  // wraps a color-swatch row underneath when the color toggle is open.
  selectionToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    maxWidth: 220,
    // Bounded height keeps MUI's Popover happy (it warns otherwise) and lets the
    // expanded color grid scroll on very short viewports.
    maxHeight: '50vh',
    overflowY: 'auto',
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.custom.panel,
    border: `1px solid ${theme.palette.custom.line}`,
    borderRadius: 8,
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.5)',
  },
  selectionColorRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: theme.spacing(0.5),
    width: '100%',
    padding: theme.spacing(0.5, 0.25, 0.25),
    marginTop: theme.spacing(0.25),
    borderTop: `1px solid ${theme.palette.custom.line}`,
  },
  // --- @-mention autocomplete popover ------------------------------------
  mentionPopover: {
    minWidth: 200,
    maxWidth: 280,
    maxHeight: '40vh',
    overflowY: 'auto',
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.custom.panel,
    border: `1px solid ${theme.palette.custom.line}`,
    borderRadius: 8,
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.5)',
  },
  mentionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    padding: theme.spacing(0.5, 0.75),
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    color: theme.palette.text.primary,
    fontFamily: 'inherit',
    fontSize: '0.85rem',
    textAlign: 'left',
    cursor: 'pointer',
  },
  mentionRowActive: {
    backgroundColor: theme.palette.custom.accentWash,
  },
  mentionAvatar: {
    width: 24,
    height: 24,
    fontSize: '0.75rem',
  },
  mentionName: {
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mentionTag: {
    backgroundColor: theme.palette.error.main,
    color: '#ffffff',
    fontSize: '0.625rem',
    height: 16,
    marginLeft: theme.spacing(0.5),
  },
  inputSection: {
    // Transparent (mockup `.composer`): shares the chat column surface, with a
    // hairline top border for separation. The input PILL below is the solid bit.
    backgroundColor: 'transparent',
    borderTop: `1px solid ${theme.palette.custom.line}`,
    padding: theme.spacing(1),
    flexShrink: 0, // keep the toolbar + input row pinned to the bottom
    // Column so a user-dragged minHeight is filled by the input row (not left as
    // dead space below a short pill). The editor grows to take the slack.
    display: 'flex',
    flexDirection: 'column',
  },
  // The draggable resize handle above the composer. Mirrors the look of the
  // react-reflex horizontal splitter (App.css .reflex-splitter.horizontal) for
  // consistency with the video/chat split.
  composerResizer: {
    flexShrink: 0,
    height: 10,
    cursor: 'row-resize',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    position: 'relative',
    transition: 'background-color 120ms',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
    // Centered grip bar.
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 32,
      height: 2,
      borderRadius: 1,
      backgroundColor: 'rgba(255, 255, 255, 0.35)',
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.custom.line}`,
  },
  toolbarButton: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5),
    minWidth: 'auto',
    width: 28,
    height: 28,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      color: theme.palette.text.primary,
    },
    '&.active': {
      // backgroundColor: theme.palette.primary.main,
      // color: theme.palette.custom.accentContrastText,
    },
  },
  // Undo/Redo: tint the icon with the accent when clickable so it reads as
  // actionable; MUI's disabled state dims it when the stack is empty.
  drawHistoryButton: {
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
    },
  },
  toolbarDivider: {
    width: 1,
    height: 29,
    backgroundColor: theme.palette.custom.line,
    marginLeft: 10,
  },
  inputContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
    // Fill any slack when the composer has been resized taller; harmless (nothing
    // to fill) at the default auto height.
    flex: 1,
    minHeight: 0,
  },
  // The composer pill that wraps the Lexical editor (was the MUI TextField).
  textInput: {
    flex: 1,
    // Solid raised pill on the translucent composer (mockup `.composer-box`).
    backgroundColor: theme.palette.custom.composerBoxBg,
    border: `1px solid ${theme.palette.custom.line}`,
    borderRadius: 4,
    transition: 'border-color 120ms',
    // Column + full height so a resized (taller) composer is filled by the
    // editable surface rather than leaving dead space below a short pill.
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    alignSelf: 'stretch',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
  },
  // The Lexical editable surface (scrolls up to ~4 lines, like the old maxRows).
  editorShell: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  editorContentEditable: {
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    lineHeight: 1.4,
    padding: '8.5px 14px',
    // Default cap (~4 lines, like the old maxRows). ChatPanel overrides this via
    // an inline `maxHeight: 100%` + flex-fill when the composer is user-resized.
    maxHeight: '6em',
    flex: 1,
    overflowY: 'auto',
    outline: 'none',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    // Live formatting preview (theme.text.* classes from MessageEditor).
    '& .composer-bold': { fontWeight: 700 },
    '& .composer-italic': { fontStyle: 'italic' },
    '& .composer-underline': { textDecoration: 'underline' },
    '& .composer-paragraph': { margin: 0 },
    // Mention pill colors, theme-aware, matching the display pill.
    '& .mention': {
      '--mention-color': theme.palette.primary.main,
      '--mention-bg': theme.palette.custom.accentWash,
    },
  },
  editorPlaceholder: {
    position: 'absolute',
    top: '8.5px',
    left: '14px',
    color: theme.palette.text.secondary,
    opacity: 0.7,
    fontSize: '0.875rem',
    lineHeight: 1.4,
    pointerEvents: 'none',
    userSelect: 'none',
  },
  // Full chat-pane overlay shown while a file is dragged over the chat area
  // (Discord-style): dark scrim + centered image icon so focus snaps to the
  // drop action. pointer-events:none so it never eats the drop hit-test.
  chatDropOverlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    backgroundColor: 'rgba(0, 0, 0, 0.62)',
    backdropFilter: 'blur(1px)',
    pointerEvents: 'none',
    userSelect: 'none',
  },
  chatDropInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(4),
    borderRadius: 12,
    border: `2px dashed ${theme.palette.primary.main}`,
    color: theme.palette.text.primary,
    textAlign: 'center',
  },
  chatDropIcon: {
    fontSize: 56,
    color: theme.palette.primary.main,
  },
  chatDropTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
  },
  chatDropHint: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
  },
  // Brief notice under the composer when a dropped image is rejected (oversize).
  editorDropError: {
    marginTop: 4,
    fontSize: '0.75rem',
    color: theme.palette.custom.ember,
  },
  sendButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.custom.accentContrastText,
    fontWeight: 600,
    minWidth: 80,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  welcomeMessage: {
    padding: theme.spacing(1),
    textAlign: 'center',
    backgroundColor: theme.palette.custom.accentWash,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    margin: theme.spacing(0.5, 1),
    borderRadius: 4,
    // Scale the banner text with the viewport font size (1em = container base).
    '& .MuiTypography-root': { fontSize: '1em' },
    '& .username': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  systemMessage: {
    textAlign: 'center',
    padding: theme.spacing(1),
    fontSize: '0.857em', // 0.75/0.875 of the viewport base — scales with font size
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    '& .MuiTypography-root': { fontSize: 'inherit' },
  },
  // Inline cam-request approval notice — same centered-banner look as the
  // welcome message, with an Accept/Deny/Mute button row beneath the text.
  camRequestMessage: {
    padding: theme.spacing(1),
    textAlign: 'center',
    backgroundColor: theme.palette.custom.accentWash,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    margin: theme.spacing(0.5, 1),
    borderRadius: 4,
    '& .MuiTypography-root': { fontSize: '1em' },
    '& .username': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  camRequestActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.75),
  },
  camRequestBtn: {
    border: 'none',
    borderRadius: 4,
    padding: theme.spacing(0.5, 1.75),
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    color: '#0f1115',
    '&.accept': { backgroundColor: '#50ce84', '&:hover': { backgroundColor: '#45b574' } },
    '&.deny': { backgroundColor: '#f0c040', '&:hover': { backgroundColor: '#d9ab2e' } },
    '&.mute': {
      backgroundColor: '#ff5252',
      color: '#ffffff',
      '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
    },
    // Neutral grey for "Ignore" (silent dismiss) — distinct from the red mute
    // and yellow deny so it doesn't read as a destructive action.
    '&.ignore': {
      backgroundColor: 'rgba(255,255,255,0.18)',
      color: '#ffffff',
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.28)' },
    },
  },
  // Active file-transfer progress line (sending/receiving). Same inset framing
  // as the cam-request banner, with a thin progress bar beneath the label.
  fileTransferProgress: {
    padding: theme.spacing(0.75, 1),
    margin: theme.spacing(0.5, 1),
    borderRadius: 4,
    backgroundColor: theme.palette.custom.accentWash,
    borderLeft: `3px solid ${theme.palette.secondary.main}`,
    '& .MuiTypography-root': { fontSize: '0.85em' },
  },
  fileTransferBar: {
    marginTop: theme.spacing(0.5),
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.15)',
    overflow: 'hidden',
  },
  fileTransferBarFill: {
    height: '100%',
    backgroundColor: theme.palette.secondary.main,
    transition: 'width 0.15s linear',
  },
  connectionStatus: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    fontSize: '0.75rem',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    flexShrink: 0,
  },
  reconnectButton: {
    flexShrink: 0,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.custom.accentWash,
    },
  },
  connectionError: {
    color: theme.palette.error.main,
  },
  connectionConnected: {
    color: theme.palette.success.main || '#4caf50',
  },
  connectionConnecting: {
    color: theme.palette.warning.main || '#ff9800',
  },
}));

const ChatPanel = ({
  messages = [],
  onSendMessage,
  onSendImage,
  onTyping,
  onlineUsers = [],
  currentUserId = null,
  isConnected = false,
  connectionError,
  isConnecting = false,
  pendingMessageCount = 0,
  onReconnect,
  currentColor = null,
  onColorChange,
  viewMode = 'default',
  onViewModeChange,
  fontSize = 'medium',
  timestampFormat = 'clock',
  pendingCamRequests = [],
  onAcceptCamRequest,
  onDenyCamRequest,
  pendingCamInvites = [],
  onAcceptCamInvite,
  onDenyCamInvite,
  onIgnoreCamInvite,
  pendingFileOffers = [],
  onAcceptFileOffer,
  onDenyFileOffer,
  fileTransfers = {},
  // userId currently wearing the "noodle shame" jester hat (or null).
  noodledUserId = null,
  // Summons App's shared user context menu: (userId, {top,left}). Same menu
  // the sidebar rows open — chat usernames are just a second summon surface.
  onOpenUserMenu,
  // Ignore / Mute v1 — user-ids the viewer has muted. Their messages are hidden
  // at RENDER time only (never removed from `messages`), so unignoring resurrects
  // history already in memory. A Set from App.
  ignoredUserIds = new Set(),
  // Quick Replies — the user's saved composer snippets, the ACKED server list
  // from App ([{ id, text, uses, createdAt }], array order = display order).
  quickReplies = [],
  // Persist a full-list replace (add/edit/delete/reorder); Promise from App
  // that re-renders `quickReplies` from the server ack.
  onQuickRepliesChange,
  // Guest capability gate for images (tenant flag, default OFF for guests).
  // Defaults true so a missing prop never hides the control from a member.
  // When false: no attach button, no drop overlay, paste ignored, drawing send
  // refused. The server enforces the same rule — this only stops a guest from
  // seeing a control that would 403.
  canUploadImages = true
}) => {
  const { classes, cx, theme } = useStyles();
  // Theme-aware mention pill styles, passed into renderRichText (which stays
  // theme-agnostic). Self-mentions get a stronger fill so you spot your own ping.
  const mentionStyles = useMemo(() => {
    const accent = theme.palette.primary.main;
    return {
      mentionStyle: {
        color: accent,
        backgroundColor: theme.palette.custom.accentWash,
        borderRadius: 4,
        padding: '0 3px',
        fontWeight: 600,
      },
      selfMentionStyle: {
        color: theme.palette.custom.accentContrastText,
        backgroundColor: accent,
        borderRadius: 4,
        padding: '0 3px',
        fontWeight: 700,
      },
    };
  }, [theme]);

  // userId -> the mentioned user's own username color (chosen or role default),
  // so an @-mention renders in THAT user's color rather than a flat accent.
  // Built from online users plus everyone seen authoring in the transcript, so
  // mentions of offline users still color correctly when their messages exist.
  const mentionColors = useMemo(() => {
    const map = {};
    for (const u of onlineUsers || []) {
      if (u && u.id) map[String(u.id)] = resolveUsernameColor(u.usernameColor, u.role);
    }
    for (const m of messages || []) {
      const u = m && m.user;
      if (u && u.id && map[String(u.id)] == null) {
        map[String(u.id)] = resolveUsernameColor(u.usernameColor, u.role);
      }
    }
    return map;
  }, [onlineUsers, messages]);

  // --- Chat-username context menu (the shared UserContextMenu in App) -------
  // Only ONLINE authors can summon it (v1 limitation — the legacy app kept
  // offline user objects around; we don't), so gate both the handlers and the
  // pointer cursor on this set.
  const onlineUserIds = useMemo(
    () => new Set((onlineUsers || []).map((u) => u && u.id).filter(Boolean)),
    [onlineUsers]
  );
  const openUserMenuAt = (clientX, clientY, authorId) => {
    // Re-check online-ness at fire time — a long-press timer can outlive the
    // author's presence.
    if (!onOpenUserMenu || !authorId || !onlineUserIds.has(authorId)) return;
    onOpenUserMenu(authorId, { top: clientY, left: clientX });
  };
  // Long-press support for touch devices (no right-click) — same slop/cancel
  // discipline as the sidebar rows: arm a timer on touch pointer-down, open at
  // the touch point after LONG_PRESS_MS, cancel on >10px drift (that's a
  // scroll), early lift, pointer leave, or unmount.
  const nameLongPressRef = useRef(null); // { timer, startX, startY } | null
  const cancelNameLongPress = () => {
    if (nameLongPressRef.current) {
      clearTimeout(nameLongPressRef.current.timer);
      nameLongPressRef.current = null;
    }
  };
  const handleNamePointerDown = (event, authorId) => {
    if (event.pointerType !== 'touch') return; // mouse/pen use click/right-click
    const { clientX, clientY } = event;
    const timer = setTimeout(() => {
      nameLongPressRef.current = null;
      openUserMenuAt(clientX, clientY, authorId);
    }, LONG_PRESS_MS);
    nameLongPressRef.current = { timer, startX: clientX, startY: clientY };
  };
  const handleNamePointerMove = (event) => {
    const lp = nameLongPressRef.current;
    if (!lp) return;
    if (
      Math.abs(event.clientX - lp.startX) > 10 ||
      Math.abs(event.clientY - lp.startY) > 10
    ) {
      cancelNameLongPress();
    }
  };
  // Clear any pending long-press timer on unmount (avoids opening the menu
  // after the panel is gone).
  useEffect(() => cancelNameLongPress, []);

  // Lightweight mirror of "does the composer have any text" for the send-button
  // disabled state. The authoritative content lives in the Lexical editor; this
  // is updated from the editor's onTyping(wire) callback.
  const [hasText, setHasText] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [quickReplyAnchor, setQuickReplyAnchor] = useState(null);
  const [colorAnchor, setColorAnchor] = useState(null);
  const [viewAnchor, setViewAnchor] = useState(null);
  // Discord-style floating format toolbar shown over a MOUSE-selected text range
  // in the composer. Null = hidden. { top, left } in viewport coords (anchor for
  // the Popover, which we center horizontally over the selection). The rect is
  // computed inside the editor (FloatingToolbarPlugin) from the native selection.
  const [selectionPopover, setSelectionPopover] = useState(null);
  // Imperative handle onto the Lexical editor (serialize/clear/focus/insertText/
  // applyFormat/applyColor/hasSelection). Set by EditorRefPlugin.
  const editorApiRef = useRef(null);
  // True while an image upload is in flight — disables the image button and
  // shows a spinner so the user can't fire off multiple uploads at once.
  const [uploadingImage, setUploadingImage] = useState(false);
  // True while a HEIC image is being decoded to JPEG (lazy wasm) before staging.
  const [convertingImage, setConvertingImage] = useState(false);
  // Image staged in the composer (file picker or paste) — not yet sent.
  // { file: File, objectUrl: string }
  const [pendingImage, setPendingImage] = useState(null);
  // Which image is open in the full-screen PhotoViewer lightbox (resolved
  // absolute URL) | null. Set from clicking an inline IMAGE thumbnail.
  const [viewerSrc, setViewerSrc] = useState(null);
  // Brief inline notice for a rejected drag-and-drop image (e.g. oversize).
  // Self-clears after a few seconds — no dedicated toast/snackbar in this UI.
  const [imageDropError, setImageDropError] = useState(null);
  const imageDropErrorTimerRef = useRef(null);
  // True while a file is dragged over the chat pane — drives the drop overlay.
  // Uses a depth counter so moving over child nodes doesn't flicker it off.
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  // Drawing mode: keyboard icon -> palette; opens a freehand canvas whose PNG
  // is sent through the existing image path (onSendImage).
  const [drawingOpen, setDrawingOpen] = useState(false);
  const [drawStroke, setDrawStroke] = useState(6); // brush line width px (slider 1..MAX_BRUSH_SIZE)
  const [drawColor, setDrawColor] = useState('#00d9ff');
  const [drawErase, setDrawErase] = useState(false);
  const [widthAnchor, setWidthAnchor] = useState(null);
  const brushSliderRef = useRef(null);
  const brushWheelCleanupRef = useRef(null);
  const [brushColorAnchor, setBrushColorAnchor] = useState(null);
  // Custom-hex draft for the brush color field (mirrors AvatarPickerDialog's
  // free hex entry). Seeded from the current brush color, sans '#'.
  const [brushHexDraft, setBrushHexDraft] = useState('00d9ff');
  const [bgColorAnchor, setBgColorAnchor] = useState(null);
  // Custom-hex draft for the canvas-background field (same free hex entry as the
  // avatar/brush picker). Seeded from the current background when opened.
  const [bgHexDraft, setBgHexDraft] = useState('');
  // Canvas background. Strokes live on a transparent canvas (so the eraser truly
  // erases); this color is shown via CSS and flattened under the strokes at export.
  // Defaults to the theme's composer surface so it repaints when the theme
  // switches — until the user explicitly picks a background from the swatch
  // grid, after which we leave their choice alone (drawBgPicked latch).
  const [drawBg, setDrawBg] = useState(theme.palette.custom.composerBoxBg);
  const [drawBgPicked, setDrawBgPicked] = useState(false);
  const drawCanvasRef = useRef(null);
  const drawingRef = useRef(false); // pointer-down latch
  // Vector stroke history (undo/redo). We record strokes as point lists + style
  // and REPLAY them rather than snapshotting the bitmap, so undo is cheap and
  // survives canvas resizes. `currentStrokeRef` is the in-progress stroke.
  const strokesRef = useRef([]); // committed strokes
  const redoRef = useRef([]); // strokes popped by undo, available to redo
  const currentStrokeRef = useRef(null);
  // Strokes/redo live in refs (no re-render per drawn point). These mirror the
  // stack depths into state so the Undo/Redo buttons enable/disable; updated
  // only on commit/undo/redo/clear via syncDrawHistory().
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Repaint the canvas background to the new theme's composer surface when the
  // theme switches — unless the user has picked their own background.
  const composerBoxBg = theme.palette.custom.composerBoxBg;
  useEffect(() => {
    if (!drawBgPicked) setDrawBg(composerBoxBg);
  }, [composerBoxBg, drawBgPicked]);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatPanelRef = useRef(null); // outer flex column, used to clamp resize

  // --- Composer resize ----------------------------------------------------
  // A drag handle above the composer lets the user grow/shrink the input area.
  // The chosen height becomes the composer's minHeight for the rest of the
  // SESSION (sessionStorage — survives reloads in this tab, resets in a fresh
  // one), matching the reflex-splitter feel used for the video/chat split.
  const COMPOSER_MIN_H = 64; // floor: a single-row composer
  const VIEWPORT_FLOOR = 120; // never let the message list be crushed below this
  const composerStorageKey = useMemo(() => {
    try {
      const info = getTenantInfoFromURL();
      return info?.tenantId ? `chatComposerHeight_${info.tenantId}` : null;
    } catch (e) {
      return null;
    }
  }, []);
  const [composerHeight, setComposerHeight] = useState(() => {
    if (!composerStorageKey) return null;
    try {
      const saved = sessionStorage.getItem(composerStorageKey);
      const n = saved != null ? parseInt(saved, 10) : NaN;
      return Number.isFinite(n) ? n : null;
    } catch (e) {
      return null;
    }
  });
  // Persist the height for the session whenever it changes.
  useEffect(() => {
    if (!composerStorageKey || composerHeight == null) return;
    try {
      sessionStorage.setItem(composerStorageKey, String(composerHeight));
    } catch (e) {
      /* private mode / quota — ignore */
    }
  }, [composerStorageKey, composerHeight]);
  const resizingRef = useRef(false);
  const resizeStartRef = useRef({ startY: 0, startHeight: 0 });

  const clampComposerHeight = (h) => {
    const panel = chatPanelRef.current;
    // Upper bound: panel height minus a floor for the message list. Fall back to
    // 60% of the window if the panel isn't measurable yet.
    const panelH = panel ? panel.getBoundingClientRect().height : window.innerHeight;
    const max = Math.max(COMPOSER_MIN_H, panelH - VIEWPORT_FLOOR);
    return Math.round(Math.min(max, Math.max(COMPOSER_MIN_H, h)));
  };

  // Track the panel's live height so the SAVED height is re-clamped when it is
  // APPLIED, not just when it was dragged. A value saved at a taller window (or
  // before the webcam strip shrank the chat pane) must not overflow the panel —
  // that pushed the composer's bottom row (e.g. "Send drawing") off-screen.
  // Measured synchronously on mount (rAF/observer delivery is suspended in
  // hidden tabs), then kept fresh by a ResizeObserver. Setting state directly in
  // the observer is safe here: minHeight changes the panel's CHILDREN, never the
  // observed panel itself, so no notify→resize loop — and the prev===h guard
  // would stop one anyway.
  const [panelHeight, setPanelHeight] = useState(null);
  useEffect(() => {
    const el = chatPanelRef.current;
    if (!el) return undefined;
    const read = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      setPanelHeight((prev) => (prev === h ? prev : h));
    };
    read(); // seed immediately — don't wait for the observer's first delivery
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The minHeight actually applied to the composer. The raw saved value stays in
  // state/storage (so growing the window back restores it); only the applied
  // value is capped by what the current panel can fit.
  const appliedComposerHeight =
    composerHeight == null
      ? null
      : panelHeight == null
        ? composerHeight
        : Math.min(composerHeight, Math.max(COMPOSER_MIN_H, panelHeight - VIEWPORT_FLOOR));

  const handleResizeMove = (clientY) => {
    if (!resizingRef.current) return;
    const { startY, startHeight } = resizeStartRef.current;
    // Dragging UP (clientY decreases) grows the composer.
    setComposerHeight(clampComposerHeight(startHeight + (startY - clientY)));
  };
  const onResizeMouseMove = (e) => handleResizeMove(e.clientY);
  const onResizeTouchMove = (e) => {
    if (e.touches && e.touches[0]) handleResizeMove(e.touches[0].clientY);
  };
  const endComposerResize = () => {
    resizingRef.current = false;
    document.body.classList.remove('cc-resizing-row');
    window.removeEventListener('mousemove', onResizeMouseMove);
    window.removeEventListener('mouseup', endComposerResize);
    window.removeEventListener('touchmove', onResizeTouchMove);
    window.removeEventListener('touchend', endComposerResize);
    window.removeEventListener('touchcancel', endComposerResize);
  };
  const startComposerResize = (clientY) => {
    // Seed from the current rendered composer height so the drag is continuous
    // even on the first drag (before composerHeight has ever been set).
    const measured =
      inputSectionRef.current?.getBoundingClientRect().height ?? COMPOSER_MIN_H;
    resizeStartRef.current = { startY: clientY, startHeight: measured };
    resizingRef.current = true;
    document.body.classList.add('cc-resizing-row');
    window.addEventListener('mousemove', onResizeMouseMove);
    window.addEventListener('mouseup', endComposerResize);
    window.addEventListener('touchmove', onResizeTouchMove, { passive: false });
    window.addEventListener('touchend', endComposerResize);
    window.addEventListener('touchcancel', endComposerResize);
  };
  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    startComposerResize(e.clientY);
  };
  const handleResizeTouchStart = (e) => {
    if (e.touches && e.touches[0]) startComposerResize(e.touches[0].clientY);
  };
  const inputSectionRef = useRef(null);
  // Safety net: drop any lingering window listeners if we unmount mid-drag.
  useEffect(() => endComposerResize, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Scroll-lock state --------------------------------------------------
  // Whether the list is pinned at (near) the bottom. When the user scrolls up
  // to read history we stop auto-following new messages, mark a "lock boundary"
  // (the id of the last message they'd seen), and count arrivals past it; an
  // inline "N new messages" divider renders at the boundary and a "Jump to
  // latest" pill appears. Returning to the bottom clears all of this.
  const NEAR_BOTTOM_PX = 80; // within this many px of the end counts as "at bottom"
  const [atBottom, setAtBottom] = useState(true);
  const atBottomRef = useRef(true);
  atBottomRef.current = atBottom;
  // Subscribe to window state so we can re-pin to the bottom when the minimized-
  // IM dock appears/disappears (it's a flow row that resizes the message list).
  const { windows: wmWindows } = useWindowState();
  // The message the user was last looking at when they scrolled up. The unread
  // count is DERIVED from this each render (see below) rather than accumulated,
  // so it stays correct even as old messages fall off the capped message window.
  const [lockBoundaryId, setLockBoundaryId] = useState(null);

  // Drives relative-timestamp refresh: bumped on an interval so "Nm ago"
  // advances without a new message. Only meaningful when timestampFormat is
  // 'relative'; the interval is a no-op otherwise.
  const [nowTick, setNowTick] = useState(() => Date.now());

  // Revoke the object URL when pendingImage changes or component unmounts.
  const prevPendingImageUrl = useRef(null);
  useEffect(() => {
    const prev = prevPendingImageUrl.current;
    if (prev && prev !== (pendingImage?.objectUrl)) {
      URL.revokeObjectURL(prev);
    }
    prevPendingImageUrl.current = pendingImage?.objectUrl || null;
    return () => {
      if (pendingImage?.objectUrl) URL.revokeObjectURL(pendingImage.objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingImage?.objectUrl]);

  // Clear the self-clearing image-drop-error timer on unmount.
  useEffect(() => () => {
    if (imageDropErrorTimerRef.current) clearTimeout(imageDropErrorTimerRef.current);
  }, []);

  const isTesting = false;

  // Mock messages based on live implementation
  // const testMockMessages = [
  //   { 
  //     id: '1755409733668',
  //     userId: '227401', 
  //     username: 'YukonBikerGuy', 
  //     avatar: 'https://html5-chat.com/upload/thumb/227401.jpg',
  //     content: '350', 
  //     timestamp: '2 hours ago',
  //     role: 'moderator',
  //     textColor: '#008080'
  //   },
  //   { 
  //     id: '1755409734960',
  //     userId: '227401', 
  //     username: 'YukonBikerGuy', 
  //     avatar: 'https://html5-chat.com/upload/thumb/227401.jpg',
  //     content: '350', 
  //     timestamp: '2 hours ago',
  //     role: 'moderator',
  //     textColor: '#008080'
  //   },
  //   { 
  //     id: '1755409740177',
  //     userId: '227302', 
  //     username: 'hashbean', 
  //     avatar: '/upload/thumb/227302.jpg',
  //     content: '350', 
  //     timestamp: '2 hours ago',
  //     role: 'user',
  //     textColor: '#ff0000'
  //   },
  //   { 
  //     id: '1755409947613',
  //     userId: '227302', 
  //     username: 'hashbean', 
  //     avatar: '/upload/thumb/227302.jpg',
  //     content: 'Looking good in here today! 🔥', 
  //     timestamp: '2 hours ago',
  //     role: 'user',
  //     textColor: '#ff0000'
  //   },
  // ]

  const mockMessages = useMemo(() => [], []);

  // Client-only "clear my screen": when a user clears the transcript (e.g. to get
  // an offensive message off their view), we anchor a cutoff to the id of the LAST
  // message loaded at that moment. Rendering then hides every message up to and
  // including that one, so the entire current transcript disappears while anything
  // that arrives afterward still shows. Anchoring by id (not by count) stays
  // correct even if the list is mutated in the middle. Nothing is sent to the
  // server — this never affects other users or the DB.
  const [clearedUpToId, setClearedUpToId] = useState(null);

  // Use real messages if available, otherwise show mock data for demo
  const sourceMessages = useMemo(() => {
    return messages.length > 0 ? messages : (isConnected ? [] : isTesting ? mockMessages : []);
  }, [messages, isConnected, isTesting, mockMessages]);

  // Apply the client-side clear cutoff: drop everything up to and including the
  // cleared anchor. If the anchor is no longer in the list (e.g. a room switch
  // loaded a fresh history), the cutoff is stale and we show everything again.
  const displayMessages = useMemo(() => {
    let list = sourceMessages;
    if (clearedUpToId) {
      const cut = list.findIndex((m) => m.id === clearedUpToId);
      if (cut !== -1) list = list.slice(cut + 1);
    }
    // Ignore / Mute: hide messages from muted users at render time only — the
    // underlying `messages` state is untouched, so unignoring resurrects them.
    // SYSTEM notices have no author; WELCOME/FAREWELL are system notices too, so
    // filtering by user id naturally leaves those alone.
    if (ignoredUserIds.size > 0) {
      list = list.filter((m) => !(m.user?.id && ignoredUserIds.has(m.user.id)));
    }
    return list;
  }, [sourceMessages, clearedUpToId, ignoredUserIds]);

  // Hide the entire current transcript from this viewer (client-only). The button
  // is disabled when there's nothing left to clear.
  const handleClearMessages = () => {
    const last = sourceMessages[sourceMessages.length - 1];
    if (!last) return;
    setClearedUpToId(last.id);
    setLockBoundaryId(null);
    setAtBottom(true);
  };

  // Scroll the list to the newest message and re-engage following (clears the
  // scroll-lock boundary; the derived unread count then becomes 0).
  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
    setAtBottom(true);
    setLockBoundaryId(null);
  };

  // Recompute whether we're near the bottom on every scroll. Returning to the
  // bottom clears the lock boundary so the divider/pill vanish.
  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distance <= NEAR_BOTTOM_PX;
    setAtBottom(nearBottom);
    if (nearBottom) setLockBoundaryId(null);
  };

  // Land at the bottom on first paint (instant, no animation).
  const didInitialScrollRef = useRef(false);
  useEffect(() => {
    if (!didInitialScrollRef.current && displayMessages.length > 0) {
      didInitialScrollRef.current = true;
      scrollToBottom('auto');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMessages.length]);

  // React to new messages: follow if at bottom (or it's our own send),
  // otherwise mark the scroll-lock boundary at the last message the user had
  // seen. The unread COUNT is derived from this boundary's position each render
  // (below), so it's correct even as the capped message window drops old rows.
  const prevLastIdRef = useRef(null);
  const prevLenRef = useRef(0);
  useEffect(() => {
    const len = displayMessages.length;
    const last = len > 0 ? displayMessages[len - 1] : null;
    const lastId = last?.id ?? null;
    const arrived = len > prevLenRef.current;
    const ownSend =
      arrived && last && currentUserId && last.user?.id === currentUserId;

    if (!didInitialScrollRef.current) {
      // initial-scroll effect handles the first paint
    } else if (atBottomRef.current || ownSend) {
      scrollToBottom(ownSend ? 'auto' : 'smooth');
    } else if (arrived) {
      // Scrolled up while new messages came in: pin the boundary once, at the
      // message the user was last looking at.
      setLockBoundaryId((cur) => cur ?? prevLastIdRef.current);
    }

    prevLastIdRef.current = lastId;
    prevLenRef.current = len;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMessages]);

  // The minimized-IM dock (Taskbar) is a flow row between the messages and the
  // composer, so showing/hiding it (or it wrapping to more rows) shrinks/grows
  // the scroll viewport. When that happens while the user is pinned to the
  // bottom, re-scroll so the newest message stays in view instead of being
  // pushed behind the dock. We key off the COUNT of minimized windows so this
  // fires on every minimize/restore. requestAnimationFrame waits for the new
  // layout to settle before scrolling.
  const minimizedCount = Object.values(wmWindows).filter((w) => w.minimized).length;
  useEffect(() => {
    if (!atBottomRef.current) return undefined;
    const raf = requestAnimationFrame(() => scrollToBottom('auto'));
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimizedCount]);

  // Unread count + boundary position DERIVED from the current message window.
  // boundaryIndex is the index of the last-seen message; everything after it is
  // unread. If the boundary has scrolled off the top of the capped window, treat
  // all visible messages as unread (boundaryIndex = -1 → count = full length).
  const boundaryIndex = lockBoundaryId
    ? displayMessages.findIndex((m) => m.id === lockBoundaryId)
    : null;
  const unreadCount =
    boundaryIndex === null ? 0 : displayMessages.length - 1 - boundaryIndex;

  // Tick once a minute so relative timestamps stay current; only schedule it
  // when the relative format is active (otherwise it would re-render for nothing).
  // `nowTick` is only advanced by this interval, so it goes stale whenever relative
  // mode is inactive — including the whole time since mount if the user starts in
  // clock/off mode. Re-read the clock IMMEDIATELY on (re)entering relative mode,
  // before the first 60s tick, otherwise an old message reads "Now" against a
  // stale reference until the first interval fires (up to a minute later).
  useEffect(() => {
    if (timestampFormat !== 'relative') return undefined;
    setNowTick(Date.now());
    const id = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, [timestampFormat]);

  const handleSendMessage = async () => {
    const wire = editorApiRef.current ? editorApiRef.current.serialize() : '';
    const trimmed = wire.trim();
    const hasImage = Boolean(pendingImage);
    if (!trimmed && !hasImage) return;

    // Image SEND needs a live backend to upload the file first, so it stays
    // gated on the connection — the pending image is kept so it can be sent
    // once reconnected. Text, by contrast, is queued by socketService while
    // offline and flushed on reconnect, so it may proceed even when down.
    if (hasImage && isConnected && onSendImage) {
      setUploadingImage(true);
      try {
        await onSendImage(pendingImage.file);
      } finally {
        setUploadingImage(false);
      }
      setPendingImage(null);
    }

    if (trimmed && onSendMessage) {
      onSendMessage(trimmed);
      if (editorApiRef.current) editorApiRef.current.clear();
      setHasText(false);
    }
  };

  // Typing in the editor: track whether there's any text (for the send button)
  // and forward the serialized wire string to the parent's presence signal.
  const handleEditorTyping = (wire) => {
    setHasText(Boolean(wire && wire.trim()));
    if (onTyping) onTyping(wire);
  };

  // The editor (FloatingToolbarPlugin) reports the viewport rect of a mouse-made
  // selection, or null when it collapses. That drives the floating format bar.
  const handleSelectionRect = (rect) => {
    if (rect) {
      setSelectionPopover(rect);
    } else {
      setSelectionPopover(null);
    }
  };

  // Wrap the current selection in a format (Bold/Italic/Underline) via the
  // editor's imperative API. Lexical toggles the format on the live selection.
  const applyFormat = (type) => {
    if (editorApiRef.current) editorApiRef.current.applyFormat(type);
  };

  const closeSelectionPopover = () => {
    setSelectionPopover(null);
  };

  // Dismiss the floating toolbar on any interaction outside it (the editor keeps
  // its selection alive while the focus-disabled Popover is open, so we close it
  // ourselves via a document mousedown listener rather than a modal backdrop).
  useEffect(() => {
    if (!selectionPopover) return undefined;
    const onDocMouseDown = (e) => {
      const toolbar = document.querySelector('.MuiPopover-paper');
      if (toolbar && toolbar.contains(e.target)) return; // click inside the bar
      closeSelectionPopover();
    };
    const onWindowChange = () => closeSelectionPopover();
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('resize', onWindowChange);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('resize', onWindowChange);
    };
  }, [selectionPopover]);

  const handleEmojiOpen = (e) => setEmojiAnchor(e.currentTarget);
  const handleEmojiClose = () => setEmojiAnchor(null);

  const handleEmojiSelect = (emojiData) => {
    if (editorApiRef.current) editorApiRef.current.insertText(emojiData.emoji);
    setEmojiAnchor(null);
  };

  // --- Quick Replies (toolbar popover + save-from-message) -----------------
  // Insert a snippet from the popover: same imperative hook as emoji, plus the
  // fire-and-forget use counter, then close and refocus the composer.
  const handleQuickReplyInsert = (reply) => {
    if (editorApiRef.current) {
      editorApiRef.current.insertText(reply.text);
      editorApiRef.current.focus();
    }
    socketService.markQuickReplyUsed(reply.id);
    setQuickReplyAnchor(null);
  };

  // "Save as quick reply" on an own TEXT message: flatten the wire content to
  // plain text (entities decoded, `@[name](id)` → `@name`) and append it. The
  // cap/dupe guards mirror the disabled state on the button; the server still
  // has the final say (the catch keeps a rejection from being an unhandled
  // promise — the list simply doesn't change).
  const handleSaveAsQuickReply = (message) => {
    const text = wireToPlainText(message.content).trim().slice(0, QUICK_REPLY_MAX_LEN);
    if (!text || !onQuickRepliesChange) return;
    if (quickReplies.length >= QUICK_REPLY_MAX) return;
    if (quickReplies.some((r) => r.text === text)) return;
    onQuickRepliesChange([...quickReplies, { text }]).catch(() => {});
  };

  const handleColorOpen = (e) => setColorAnchor(e.currentTarget);
  const handleColorClose = () => setColorAnchor(null);

  const handleColorSelect = (hex) => {
    setColorAnchor(null);

    // Make this the active default — the whole message (and future messages)
    // renders in it, persisted via onColorChange.
    if (onColorChange) onColorChange(hex);

    // If text is highlighted, also color just that span inline (overrides the
    // default for those characters). With no selection there's nothing to wrap —
    // the default color already covers the whole message.
    if (editorApiRef.current && editorApiRef.current.hasSelection()) {
      editorApiRef.current.applyColor(hex);
    }
  };

  // Message-layout menu (chat-section header). A per-viewer reading preference.
  const handleViewMenuOpen = (e) => setViewAnchor(e.currentTarget);
  const handleViewMenuClose = () => setViewAnchor(null);
  const handleViewSelect = (value) => {
    setViewAnchor(null);
    if (onViewModeChange) onViewModeChange(value);
  };
  const activeViewMode =
    VIEW_MODE_OPTIONS.find((o) => o.value === viewMode) || VIEW_MODE_OPTIONS[0];

  // Stage a file as the pending image (picker, paste, or drag-and-drop). This is
  // the single gate for every entry point: it converts HEIC->JPEG (lazy-loading
  // the decoder), validates size/type, then stages the browser-renderable file.
  const stageImage = async (file) => {
    if (!file) return;
    // Guest gate. stageImage is the common funnel for the picker, plain drop and
    // paste, so one check here covers all three without a per-handler branch.
    if (!canUploadImages) return;
    setConvertingImage(true);
    try {
      const result = await prepareImageFile(file);
      if (!result.ok) {
        if (result.error) handleImageReject(result.error);
        return; // non-image with no message: ignore silently
      }
      const objectUrl = URL.createObjectURL(result.file);
      setPendingImage({ file: result.file, objectUrl });
    } finally {
      setConvertingImage(false);
    }
  };

  // Shift+drop -> prepare (HEIC convert + validate) then upload & send the image
  // immediately with no caption, mirroring handleSendMessage's image branch.
  // Plain drop stages instead (see stageImage) so a caption can be added first.
  const prepareAndSendImage = async (file) => {
    if (!file) return;
    if (!isConnected || !onSendImage || !canUploadImages) return;
    setConvertingImage(true);
    let prepared;
    try {
      const result = await prepareImageFile(file);
      if (!result.ok) {
        if (result.error) handleImageReject(result.error);
        return; // non-image with no message: ignore silently
      }
      prepared = result.file;
    } finally {
      setConvertingImage(false);
    }
    setUploadingImage(true);
    try {
      await onSendImage(prepared);
    } finally {
      setUploadingImage(false);
    }
  };

  // Image button -> open the hidden native file picker.
  const handleImageButtonClick = () => {
    if (!isConnected || uploadingImage) return;
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // A dropped image failed validation (oversize) -> brief self-clearing notice.
  const handleImageReject = (message) => {
    if (imageDropErrorTimerRef.current) clearTimeout(imageDropErrorTimerRef.current);
    setImageDropError(message);
    imageDropErrorTimerRef.current = setTimeout(() => setImageDropError(null), 4000);
  };

  // ---- Drag & drop image onto the chat pane ---------------------------------
  // The whole chat area is the drop target (Discord-style). A dragged file shows
  // a dark overlay; dropping stages the image (plain) or sends it now (Shift).
  // Also the guest gate: with canUploadImages false this suppresses the overlay,
  // sets the drag cursor to "none", and refuses the drop — one chokepoint for
  // all three, which is why the gate goes here rather than in each handler.
  const dropDisabled = () =>
    !isConnected || uploadingImage || convertingImage || !canUploadImages;
  const isFileDrag = (e) =>
    e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');

  const handleChatDragEnter = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    if (!dropDisabled()) setDragActive(true);
  };
  const handleChatDragOver = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = dropDisabled() ? 'none' : 'copy';
  };
  const handleChatDragLeave = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };
  const handleChatDrop = (e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    if (dropDisabled()) return;
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    // Shift = send immediately; otherwise stage for an optional caption.
    if (e.shiftKey) prepareAndSendImage(file);
    else stageImage(file);
  };

  // File picker selection: stage, don't send yet.
  const handleImageFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    stageImage(file);
  };

  // ---- Drawing mode ---------------------------------------------------------
  // Map a pointer event to canvas coordinates in CSS pixels (DPR-independent).
  // Storing strokes in CSS px (not backing-store px) means they replay correctly
  // after a resize — we apply the device-pixel scale only when rendering.
  const drawPos = (e) => {
    const c = drawCanvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  // Render one stroke onto the context. `scale` converts our stored CSS-px
  // coordinates/width to the canvas backing store. Single source of truth used
  // by live drawing, full redraws, undo, redo, and resize.
  const renderStroke = (ctx, stroke, scale) => {
    if (!stroke || stroke.points.length === 0) return;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // width is the line width in CSS px; ×scale for the backing store.
    ctx.lineWidth = stroke.width * scale;
    ctx.globalCompositeOperation = stroke.erase ? 'destination-out' : 'source-over';
    ctx.strokeStyle = stroke.color;
    ctx.beginPath();
    const pts = stroke.points;
    ctx.moveTo(pts[0].x * scale, pts[0].y * scale);
    if (pts.length === 1) {
      // A single tap: draw a dot by stroking a zero-length segment.
      ctx.lineTo(pts[0].x * scale, pts[0].y * scale);
    } else {
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * scale, pts[i].y * scale);
    }
    ctx.stroke();
  };

  const canvasScale = (c) => c.width / c.getBoundingClientRect().width || 1;

  // Mirror the undo/redo stack depths into state to drive button disabled state.
  const syncDrawHistory = () => {
    setCanUndo(strokesRef.current.length > 0);
    setCanRedo(redoRef.current.length > 0);
  };

  // Clear and replay every committed stroke. Used by undo/redo and resize.
  const redraw = () => {
    const c = drawCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    const scale = canvasScale(c);
    for (const s of strokesRef.current) renderStroke(ctx, s, scale);
  };

  const drawStart = (e) => {
    const c = drawCanvasRef.current;
    if (!c) return;
    e.preventDefault();
    // Take keyboard focus so undo/redo shortcuts work right after drawing.
    c.focus();
    drawingRef.current = true;
    // Begin a new stroke capturing the current style. A fresh stroke invalidates
    // the redo stack (standard editor behavior).
    currentStrokeRef.current = {
      color: drawColor,
      width: drawStroke,
      erase: drawErase,
      points: [drawPos(e)],
    };
    redoRef.current = [];
    if (c.setPointerCapture && e.pointerId != null) c.setPointerCapture(e.pointerId);
  };

  const drawMove = (e) => {
    if (!drawingRef.current) return;
    const c = drawCanvasRef.current;
    const stroke = currentStrokeRef.current;
    if (!stroke) return;
    const p = drawPos(e);
    stroke.points.push(p);
    // Draw just the new segment live (cheaper than a full redraw per move).
    const ctx = c.getContext('2d');
    const scale = canvasScale(c);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = stroke.width * scale;
    ctx.globalCompositeOperation = stroke.erase ? 'destination-out' : 'source-over';
    ctx.strokeStyle = stroke.color;
    const pts = stroke.points;
    const prev = pts[pts.length - 2];
    ctx.beginPath();
    ctx.moveTo(prev.x * scale, prev.y * scale);
    ctx.lineTo(p.x * scale, p.y * scale);
    ctx.stroke();
  };

  const drawEnd = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const stroke = currentStrokeRef.current;
    currentStrokeRef.current = null;
    if (stroke && stroke.points.length > 0) {
      strokesRef.current.push(stroke);
      syncDrawHistory();
    }
  };

  const undoDrawing = () => {
    if (strokesRef.current.length === 0) return;
    redoRef.current.push(strokesRef.current.pop());
    redraw();
    syncDrawHistory();
  };

  const redoDrawing = () => {
    if (redoRef.current.length === 0) return;
    strokesRef.current.push(redoRef.current.pop());
    redraw();
    syncDrawHistory();
  };

  // Keyboard shortcuts, scoped to canvas focus (not a document listener) so they
  // never collide with the Lexical composer's own Ctrl+Z history. Ctrl/Cmd+Z =
  // undo; Ctrl/Cmd+Shift+Z (Adobe/Figma) or Ctrl/Cmd+Y (Windows) = redo.
  const handleDrawKeyDown = (e) => {
    const mod = e.ctrlKey || e.metaKey;
    if (!mod) return;
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undoDrawing();
    } else if ((key === 'z' && e.shiftKey) || key === 'y') {
      e.preventDefault();
      redoDrawing();
    }
  };

  const clearDrawing = () => {
    const c = drawCanvasRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    strokesRef.current = [];
    redoRef.current = [];
    currentStrokeRef.current = null;
    syncDrawHistory();
  };

  const closeDrawing = () => {
    setDrawingOpen(false);
    setDrawErase(false);
  };

  // Apply a brush color from either a swatch or the custom hex field. Normalizes
  // to a 6-digit hex (applying the legacy 0 → 0x111111 remap, like the avatar
  // picker) and keeps the hex draft in sync. Turns the eraser off.
  const pickBrushColor = (color) => {
    const norm = normalizeBuiltinColor(color);
    if (!norm) return;
    setDrawColor(`#${norm}`);
    setBrushHexDraft(norm);
    setDrawErase(false);
  };

  // Free hex entry for the brush color: applies once six valid digits are typed.
  const handleBrushHexInput = (value) => {
    const clean = value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6).toLowerCase();
    setBrushHexDraft(clean);
    if (clean.length === 6) pickBrushColor(clean);
  };

  // Apply a canvas-background color (swatch or custom hex). Normalizes like the
  // avatar picker and latches drawBgPicked so the theme no longer overrides it.
  const pickBgColor = (color) => {
    const norm = normalizeBuiltinColor(color);
    if (!norm) return;
    setDrawBg(`#${norm}`);
    setBgHexDraft(norm);
    setDrawBgPicked(true);
  };

  // Free hex entry for the canvas background: applies once six digits are typed.
  const handleBgHexInput = (value) => {
    const clean = value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6).toLowerCase();
    setBgHexDraft(clean);
    if (clean.length === 6) pickBgColor(clean);
  };

  // Export the canvas to a PNG File and send via the existing image path.
  // Strokes live on a transparent canvas, so we flatten the chosen background
  // underneath them into an offscreen canvas before encoding.
  const sendDrawing = async () => {
    const c = drawCanvasRef.current;
    // The drawing canvas builds a File directly and never touches
    // prepareImageFile/stageImage, so it needs its own gate.
    if (!c || !onSendImage || !canUploadImages) return;
    const out = document.createElement('canvas');
    out.width = c.width;
    out.height = c.height;
    const octx = out.getContext('2d');
    if (drawBg !== TRANSPARENT_BG) {
      octx.fillStyle = drawBg;
      octx.fillRect(0, 0, out.width, out.height);
    }
    octx.drawImage(c, 0, 0);
    const blob = await new Promise((res) => out.toBlob(res, 'image/png'));
    if (!blob) return;
    const file = new File([blob], 'drawing.png', { type: 'image/png' });
    setUploadingImage(true);
    try {
      await onSendImage(file);
    } finally {
      setUploadingImage(false);
    }
    clearDrawing();
    closeDrawing();
  };

  // Size the canvas backing store to its rendered size (× devicePixelRatio) so
  // strokes are crisp and the exported PNG matches the client's full width.
  // Resizing a canvas clears it; we replay the vector strokes (sharp at any
  // size) rather than rescaling the old bitmap (which blurred).
  useEffect(() => {
    if (!drawingOpen) return;
    const c = drawCanvasRef.current;
    if (!c) return;
    const fit = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = Math.round(c.clientWidth * dpr);
      const h = Math.round(c.clientHeight * dpr);
      if (!w || !h || (c.width === w && c.height === h)) return;
      c.width = w;
      c.height = h;
      redraw();
    };
    fit();
    // Defer the observer's work to the next frame. Resizing the canvas backing
    // store INSIDE the ResizeObserver callback re-triggers layout in the same
    // frame, which the browser reports as the (benign) "ResizeObserver loop
    // completed with undelivered notifications" error — CRA's overlay treats it
    // as fatal. rAF lets the observer settle before we mutate size.
    let rafId = 0;
    const ro = new ResizeObserver(() => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        fit();
      });
    });
    ro.observe(c);
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      ro.disconnect();
    };
    // redraw is a stable closure over refs; re-running on its identity would
    // reset the observer every render. Only (re)attach when the canvas mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingOpen]);

  const clearPendingImage = () => setPendingImage(null);

  // Username color is a fixed, identity-scoped property — independent of the
  // message text color (message.user.textColor). The server always supplies a
  // resolved usernameColor (chosen or role default); the trailing fallbacks
  // only guard against an older payload missing the field.
  const usernameColor = (message) =>
    resolveUsernameColor(message.user?.usernameColor, message.user?.role);

  // Format a message timestamp per the user's preference. `relative` is read
  // against `nowTick` so the periodic tick below advances "2m ago" without a new
  // message; `clock` is the historical locale time; `off` is handled by the
  // caller (hidden inline, revealed on hover / long-press). A missing timestamp
  // falls back to 'now' (live-sent message before the server stamp arrives).
  const formatTime = (message) => {
    if (!message.timestamp) return 'now';
    if (timestampFormat === 'relative') {
      return formatRelativeTime(message.timestamp, nowTick) || 'now';
    }
    return formatClockTime(message.timestamp) || 'now';
  };

  // `grouped` (bubble mode only) marks a follow-up message in a same-user run:
  // we hide the avatar + name and indent it under the first bubble.
  const renderMessage = (message, grouped = false) => {
    // Handle welcome messages specially
    if (message.type === 'WELCOME') {
      return (
        <div key={message.id} className={classes.welcomeMessage}>
          <Typography variant="body2">
            Welcome <span className="username">{message.user.username}</span> into room chat
          </Typography>
        </div>
      );
    }

    // Handle farewell messages — same system-notice style as the welcome notice
    if (message.type === 'FAREWELL') {
      return (
        <div key={message.id} className={classes.welcomeMessage}>
          <Typography variant="body2">
            <span className="username">{message.user.username}</span> has left the chat
          </Typography>
        </div>
      );
    }

    // Handle system messages
    if (message.type === 'SYSTEM') {
      return (
        <div key={message.id} className={classes.systemMessage}>
          <Typography variant="body2">{message.content}</Typography>
        </div>
      );
    }

    const isCompact = viewMode === 'compact';
    const isBubble = viewMode === 'bubble';

    const resolvedAvatar = resolveAvatarUrl(message.user?.avatar);
    // "Noodle shame": this message's author is currently wearing the jester hat.
    const isNoodled = noodledUserId && message.user?.id === noodledUserId;
    const avatarEl = (
      <Avatar
        src={resolvedAvatar}
        alt={message.user?.username || 'User'}
        className={cx(
          classes.userAvatar,
          isCompact && classes.avatarCompact,
          // Always transparent behind the image — the fallback is itself an
          // image now (the theme-tinted builtin default), never a bare letter.
          classes.avatarPhoto
        )}
        data-username={message.user?.username}
        data-id={message.user?.id}
      >
        <AvatarFallback
          letter={message.user?.username?.charAt(0).toUpperCase() || '?'}
        />
      </Avatar>
    );
    // Wrap in a relative, non-clipping span only when shamed, so the hat can
    // perch above and overflow the avatar. Avatar size is em-based, so hand the
    // hat the matching em length to stay proportional at any row font size.
    const avatar = isNoodled ? (
      <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, overflow: 'visible' }}>
        {avatarEl}
        <JesterHat
          size={isCompact ? '1.143em' : '2.286em'}
          title={`${message.user?.username || 'They'} noodled it 🍜`}
        />
      </span>
    ) : (
      avatarEl
    );

    // Username summons the shared user context menu. Unlike the sidebar rows
    // there's no competing row action here, so plain LEFT-CLICK works on every
    // platform (a name in the stream is link-like); right-click and touch
    // long-press mirror the sidebar gestures. Handlers go on the username span
    // ONLY — never the message row — so text selection stays intact. Offline
    // or id-less authors get no handlers at all (WELCOME/SYSTEM rows never
    // reach this code path — they return earlier).
    const authorId = message.user?.id;
    const canSummonMenu =
      !!authorId && !!onOpenUserMenu && onlineUserIds.has(authorId);
    const usernameSpan = (
      <span
        className={classes.username}
        style={{
          color: usernameColor(message),
          ...(canSummonMenu && { cursor: 'pointer' }),
        }}
        data-id={message.user?.id}
        data-username={message.user?.username}
        {...(canSummonMenu && {
          onClick: (e) => {
            e.stopPropagation();
            openUserMenuAt(e.clientX, e.clientY, authorId);
          },
          onContextMenu: (e) => {
            e.preventDefault();
            e.stopPropagation();
            openUserMenuAt(e.clientX, e.clientY, authorId);
          },
          onPointerDown: (e) => handleNamePointerDown(e, authorId),
          onPointerMove: handleNamePointerMove,
          onPointerUp: cancelNameLongPress,
          onPointerLeave: cancelNameLongPress,
          onPointerCancel: cancelNameLongPress,
        })}
      >
        {message.user?.username || 'Unknown User'}
      </span>
    );

    // When the format is 'off', the timestamp is hidden inline and revealed on
    // hover (desktop, via the hideable class' :hover rule scoped to the row) or
    // long-press/tap (mobile, via the row's title tooltip below). Otherwise it
    // renders normally. WELCOME/SYSTEM rows don't carry a timestamp span.
    const timeText = formatTime(message);
    const timestampOff = timestampFormat === 'off';
    const timestampSpan = (
      <span
        className={classes.timestamp}
        data-date={message.id}
        data-ts-hideable={timestampOff ? 'true' : undefined}
      >
        {timeText}
      </span>
    );

    // IMAGE messages: content is a server-hosted image URL. Render a constrained
    // thumbnail (larger dimension capped at IMAGE_THUMB_MAX_PX) that opens the
    // full-size original in a new tab on click. Otherwise render rich text.
    const isImage = message.type === 'IMAGE';

    // "Save as quick reply" — own TEXT messages only, revealed on row hover
    // (the .msg-save-action rule in the `message` style). Disabled-with-title
    // covers the cap/dupe cases inline (no snackbar infra in this file).
    const isOwn = !!currentUserId && message.user?.id === currentUserId;
    let saveBtn = null;
    if (isOwn && !isImage && onQuickRepliesChange) {
      const savedText = wireToPlainText(message.content)
        .trim()
        .slice(0, QUICK_REPLY_MAX_LEN);
      const atCap = quickReplies.length >= QUICK_REPLY_MAX;
      const isDupe = quickReplies.some((r) => r.text === savedText);
      const canSave = !!savedText && !atCap && !isDupe;
      saveBtn = (
        <IconButton
          className={cx(classes.msgHoverAction, 'msg-save-action')}
          size="small"
          disabled={!canSave}
          title={
            atCap
              ? `Quick replies full (${QUICK_REPLY_MAX}/${QUICK_REPLY_MAX})`
              : isDupe
                ? 'Already a quick reply'
                : 'Save as quick reply'
          }
          aria-label="Save as quick reply"
          onClick={() => handleSaveAsQuickReply(message)}
        >
          <QuickReplyIcon fontSize="inherit" />
        </IconButton>
      );
    }
    const imageUrl = isImage ? resolveUploadUrl(message.content) : null;

    const textDiv = isImage ? (
      <div
        role="button"
        tabIndex={0}
        className={classes.imageLink}
        title="View photo"
        onClick={() => setViewerSrc(imageUrl)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setViewerSrc(imageUrl);
          }
        }}
      >
        <img src={imageUrl} alt="Shared" className={classes.imageThumb} />
      </div>
    ) : (
      <div
        className={cx(classes.messageText, isBubble && classes.bubbleText)}
        style={{ color: message.user?.textColor || '#ffffff' }}
      >
        {isCompact
          ? <>{' '}{renderRichText(message.content, { currentUserId, mentionColors, ...mentionStyles })}</>
          : renderRichText(message.content, { currentUserId, mentionColors, ...mentionStyles })}
      </div>
    );

    const rowClass = cx(
      classes.message,
      isCompact && classes.messageCompact,
      message.user?.role || 'user',
      grouped && classes.bubbleFollowUp,
    );

    return (
      <div
        key={message.id}
        className={rowClass}
        data-messageid={message.id}
        data-userid={message.user?.id}
        // When timestamps are 'off', the native title gives a long-press/tap
        // reveal on touch devices (desktop uses the CSS :hover reveal).
        title={timestampOff ? timeText : undefined}
      >
        {/* Avatar — hidden for bubble follow-ups in a same-user run */}
        {!grouped && avatar}

        {isCompact ? (
          <div className={cx(classes.messageContent, classes.compactContent)}>
            {usernameSpan}
            {textDiv}
            {timestampSpan}
            {saveBtn}
          </div>
        ) : (
          <div className={cx(classes.messageContent, isBubble && classes.bubble)}>
            {!grouped && (
              <div className={classes.usernameLine}>
                {usernameSpan}
                {timestampSpan}
                {saveBtn}
              </div>
            )}
            {textDiv}
          </div>
        )}
      </div>
    );
  };

  // Bubble mode groups consecutive messages from the same user: the name/avatar
  // show on the first of a run, follow-ups render as bare indented bubbles.
  // Runs span message types — a user's IMAGE and TEXT posts chain together — so
  // we group by author and only break the run on the system-notice types.
  // When scroll-locked, an "N new messages" divider is injected just before the
  // first message that arrived after the lock boundary.
  const NOTICE_TYPES = ['WELCOME', 'FAREWELL', 'SYSTEM'];
  const renderMessages = () =>
    displayMessages.map((message, i) => {
      const prev = displayMessages[i - 1];
      const grouped =
        viewMode === 'bubble' &&
        prev &&
        !NOTICE_TYPES.includes(prev.type) &&
        !NOTICE_TYPES.includes(message.type) &&
        prev.user?.id === message.user?.id;
      // Show the "N new messages" divider just before the first unread message.
      // Normally that's the row right after the boundary; if the boundary has
      // scrolled off the top of the capped window (boundaryIndex < 0), anchor the
      // divider before the very first visible row instead.
      const showDivider =
        unreadCount > 0 &&
        boundaryIndex !== null &&
        (boundaryIndex >= 0 ? i === boundaryIndex + 1 : i === 0);
      const row = renderMessage(message, showDivider ? false : grouped);
      if (!showDivider) return row;
      return (
        <React.Fragment key={`unread_${message.id}`}>
          <div className={classes.unreadDivider}>
            <span>
              {unreadCount} new message{unreadCount === 1 ? '' : 's'}
            </span>
          </div>
          {row}
        </React.Fragment>
      );
    });

  // Suffix shown while messages are buffered for reconnection, so a user who
  // typed during an outage can see their text is held — not lost.
  const queuedSuffix =
    pendingMessageCount > 0
      ? ` — ${pendingMessageCount} message${pendingMessageCount === 1 ? '' : 's'} will send when reconnected`
      : '';

  const getConnectionStatusText = () => {
    if (connectionError) {
      return `Connection Error: ${connectionError}${queuedSuffix}`;
    }
    if (isConnecting) {
      return `Connecting to chat...${queuedSuffix}`;
    }
    if (isConnected) {
      return 'Connected to chat';
    }
    return `Not connected${queuedSuffix}`;
  };

  const getConnectionStatusClass = () => {
    if (connectionError) return classes.connectionError;
    if (isConnecting) return classes.connectionConnecting;
    if (isConnected) return classes.connectionConnected;
    return classes.connectionError;
  };

  return (
    <Paper
      className={classes.chatPanel}
      elevation={0}
      ref={chatPanelRef}
      onDragEnter={handleChatDragEnter}
      onDragOver={handleChatDragOver}
      onDragLeave={handleChatDragLeave}
      onDrop={handleChatDrop}
    >
      {dragActive && (
        <div className={classes.chatDropOverlay}>
          <div className={classes.chatDropInner}>
            <ImageIcon className={classes.chatDropIcon} />
            <div className={classes.chatDropTitle}>Drop image to upload</div>
            <div className={classes.chatDropHint}>Hold Shift to send instantly</div>
          </div>
        </div>
      )}
      <div className={classes.header}>
        <Typography className={classes.headerText}>
          Chat
        </Typography>
        {/* Clear my screen — client-only: hides the entire current transcript
            from THIS viewer (e.g. to get an offensive post off-screen). Doesn't
            delete on the server or for anyone else; new messages still arrive. */}
        <Tooltip title="Clear all messages from my screen">
          <span className={classes.clearButton}>
            <IconButton
              onClick={handleClearMessages}
              size="small"
              disabled={displayMessages.length === 0}
              aria-label="Clear messages from my screen"
            >
              <EraserIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {/* Message layout / density — a per-viewer reading preference for the
            whole transcript, floated to the right of the section header. */}
        <Tooltip title="Message layout">
          <IconButton
            className={classes.viewModeButton}
            onClick={handleViewMenuOpen}
            size="small"
            aria-label="Message layout"
            aria-haspopup="true"
            aria-expanded={Boolean(viewAnchor)}
          >
            <activeViewMode.Icon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={viewAnchor}
          open={Boolean(viewAnchor)}
          onClose={handleViewMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { style: { maxHeight: '60vh' } } }}
        >
          {VIEW_MODE_OPTIONS.map((option) => {
            const selected = option.value === viewMode;
            return (
              <MenuItem
                key={option.value}
                selected={selected}
                onClick={() => handleViewSelect(option.value)}
                className={classes.viewMenuItem}
              >
                <ListItemIcon>
                  <option.Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={option.label} />
                {selected && (
                  <CheckIcon fontSize="small" className={classes.viewCheckIcon} />
                )}
              </MenuItem>
            );
          })}
        </Menu>
      </div>

      {/* Connection Status */}
      <div className={cx(classes.connectionStatus, getConnectionStatusClass())}>
        <span>{getConnectionStatusText()}</span>
        {connectionError && !isConnecting && onReconnect && (
          <button
            type="button"
            className={classes.reconnectButton}
            onClick={onReconnect}
          >
            Reconnect
          </button>
        )}
      </div>

      {/* id lets a maximized IM window size itself to exactly this region (the
          chat message column) instead of the whole viewport — see windowing/. */}
      <div className={classes.messagesViewport} id="im-maximize-bounds">
      <div
        className={classes.messagesContainer}
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        // Font size applies to the WHOLE viewport: descendant text uses `em`, so
        // setting the container's base size scales usernames, timestamps, message
        // text, and the welcome/system rows together. 100% = the historical base.
        style={{ fontSize: fontSizeRem(fontSize) }}
      >
        {displayMessages.length === 0 && isConnected && (
          <div className={classes.systemMessage}>
            <Typography variant="body2">
              No messages yet. Say hello to start the conversation!
            </Typography>
          </div>
        )}

        {renderMessages()}

        {/* Private-mode cam-request approval notices — rendered inline at the
            bottom of the message stream (like welcome/left notices) so multiple
            requests stack in the chat rather than piling up as toasts. */}
        {pendingCamRequests.map((req) => (
          <div key={`camreq_${req.fromUserId}`} className={classes.camRequestMessage}>
            <Typography variant="body2">
              <span className="username">{req.fromUsername}</span> is requesting to view your webcam
            </Typography>
            <div className={classes.camRequestActions}>
              <button
                className={cx(classes.camRequestBtn, 'accept')}
                onClick={() => onAcceptCamRequest && onAcceptCamRequest(req.fromUserId)}
              >
                Accept
              </button>
              <button
                className={cx(classes.camRequestBtn, 'deny')}
                onClick={() => onDenyCamRequest && onDenyCamRequest(req.fromUserId)}
              >
                Deny
              </button>
              <button
                className={cx(classes.camRequestBtn, 'mute')}
                disabled
                title="Mute (coming soon)"
              >
                Mute
              </button>
            </div>
          </div>
        ))}

        {/* Incoming cam INVITES — same banner as requests, but the invitee is
            being offered a view of the inviter's cam. Accept opens their cam;
            Deny notifies them; Ignore silently dismisses (no event sent). */}
        {pendingCamInvites.map((inv) => (
          <div key={`caminv_${inv.fromUserId}`} className={classes.camRequestMessage}>
            <Typography variant="body2">
              <span className="username">{inv.fromUsername}</span> invited you to view their webcam
            </Typography>
            <div className={classes.camRequestActions}>
              <button
                className={cx(classes.camRequestBtn, 'accept')}
                onClick={() => onAcceptCamInvite && onAcceptCamInvite(inv.fromUserId)}
              >
                Accept
              </button>
              <button
                className={cx(classes.camRequestBtn, 'deny')}
                onClick={() => onDenyCamInvite && onDenyCamInvite(inv.fromUserId)}
              >
                Deny
              </button>
              <button
                className={cx(classes.camRequestBtn, 'ignore')}
                onClick={() => onIgnoreCamInvite && onIgnoreCamInvite(inv.fromUserId)}
                title="Dismiss this invite"
              >
                Ignore
              </button>
            </div>
          </div>
        ))}

        {/* Incoming FILE offers — direct user-to-user transfer. Same banner
            style as cam requests. The filename is rendered as text (React
            escapes it), so a crafted name can't inject markup. */}
        {pendingFileOffers.map((offer) => (
          <div key={`fileoffer_${offer.transferId}`} className={classes.camRequestMessage}>
            <Typography variant="body2">
              <span className="username">{offer.fromUsername}</span> wants to send you{' '}
              <strong>{offer.name}</strong> ({formatBytes(offer.size)})
            </Typography>
            <div className={classes.camRequestActions}>
              <button
                className={cx(classes.camRequestBtn, 'accept')}
                onClick={() => onAcceptFileOffer && onAcceptFileOffer(offer)}
              >
                Accept
              </button>
              <button
                className={cx(classes.camRequestBtn, 'deny')}
                onClick={() => onDenyFileOffer && onDenyFileOffer(offer)}
              >
                Deny
              </button>
            </div>
          </div>
        ))}

        {/* Active file transfers (send + receive) — a simple progress line per
            in-flight transfer. Drops out of the list when the App clears it. */}
        {Object.entries(fileTransfers).map(([transferId, t]) => {
          const pct = t.size ? Math.min(100, Math.round((t.sent / t.size) * 100)) : 0;
          return (
            <div key={`filexfer_${transferId}`} className={classes.fileTransferProgress}>
              <Typography variant="body2">
                {t.dir === 'send' ? 'Sending' : 'Receiving'} <strong>{t.name}</strong>
                {t.peerUsername ? ` ${t.dir === 'send' ? 'to' : 'from'} ${t.peerUsername}` : ''} — {pct}%
              </Typography>
              <div className={classes.fileTransferBar}>
                <div className={classes.fileTransferBarFill} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

        {/* Scroll-lock affordance: appears only when the user has scrolled up
            and new messages have arrived. Click returns to the newest and
            resumes following. */}
        {!atBottom && unreadCount > 0 && (
          <button
            type="button"
            className={classes.jumpToLatest}
            onClick={() => scrollToBottom('smooth')}
          >
            <ArrowDownIcon fontSize="small" />
            {unreadCount} new message{unreadCount === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {/* Dock for minimized IM windows — a normal-flow row between the messages
          viewport and the composer, so minimized IMs sit just above the input
          and push it down rather than overlaying the message list. Renders
          nothing when none are minimized. */}
      <Taskbar />

      {/* Drag handle to grow/shrink the composer. Dragging up gives more typing
          room (shrinking the message list above); the chosen height persists for
          the session. Shown in both text and drawing modes — the drawing canvas
          fills the dragged height too. Styled like the reflex-splitter used for
          the video split. */}
      <div
        className={classes.composerResizer}
        onMouseDown={handleResizeMouseDown}
        onTouchStart={handleResizeTouchStart}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize message input"
        title="Drag to resize the message input"
      />

      <div
        className={classes.inputSection}
        ref={inputSectionRef}
        style={appliedComposerHeight != null ? { minHeight: appliedComposerHeight } : undefined}
      >
        <div className={classes.toolbar}>
          {/* Drawing sends its canvas as an image, so it goes with the image
              gate — leaving it would offer a mode whose only exit is a refusal. */}
          {canUploadImages && (
          <IconButton
            className={cx(classes.toolbarButton, drawingOpen && 'active')}
            onClick={() => setDrawingOpen((v) => !v)}
            size="small"
            disabled={!isConnected || uploadingImage}
            title={drawingOpen ? 'Close drawing' : 'Draw'}
            aria-label="Draw"
          >
            {drawingOpen ? <PaletteIcon fontSize="small" /> : <KeyboardIcon fontSize="small" />}
          </IconButton>
          )}
          {!drawingOpen && (<>
          {/* Bold/Italic/Underline now live in the selection format toolbar that
              floats over a highlighted range — no static toolbar buttons. */}
          <IconButton
            className={classes.toolbarButton}
            onClick={handleColorOpen}
            size="small"
            title="Text color"
            aria-label="Text color"
          >
            <ColorIcon fontSize="small" style={currentColor ? { color: currentColor } : undefined} />
          </IconButton>

          <Divider orientation="vertical" flexItem style={{ margin: '0 4px', backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />

          <IconButton
            className={classes.toolbarButton}
            onClick={handleEmojiOpen}
            size="small"
            title="Emoji"
            aria-label="Emoji"
          >
            <EmojiIcon fontSize="small" />
          </IconButton>
          <IconButton
            className={classes.toolbarButton}
            onClick={(e) => setQuickReplyAnchor(e.currentTarget)}
            size="small"
            title="Quick replies"
            aria-label="Quick replies"
          >
            <QuickReplyIcon fontSize="small" />
          </IconButton>
          {/* Attach image — hidden outright when the guest gate denies uploads,
              along with its picker, so there is no control to click and no
              input to drive programmatically. */}
          {canUploadImages && (<>
          <IconButton
            className={cx(classes.toolbarButton, pendingImage && 'active')}
            onClick={handleImageButtonClick}
            size="small"
            disabled={!isConnected || uploadingImage}
            title="Attach image"
            aria-label="Attach image"
          >
            <ImageIcon fontSize="small" />
          </IconButton>
          {/* Hidden native picker driven by the image button above. */}
          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            style={{ display: 'none' }}
            onChange={handleImageFileChange}
          />
          </>)}
          </>)}

          {/* Drawing tools replace the text-format buttons while in drawing mode:
              brush width (popover), brush color (popover), eraser toggle. */}
          {drawingOpen && (<>
          <IconButton
            className={classes.toolbarButton}
            onClick={(e) => setWidthAnchor(e.currentTarget)}
            size="small"
            title="Brush width"
            aria-label="Brush width"
          >
            <LineWeightIcon fontSize="small" />
          </IconButton>
          <IconButton
            className={classes.toolbarButton}
            onClick={(e) => {
              setBrushHexDraft(normalizeBuiltinColor(drawColor) || 'ff0000');
              setBrushColorAnchor(e.currentTarget);
            }}
            size="small"
            title="Brush color"
            aria-label="Brush color"
          >
            <ChipIcon icon={<BrushIcon fontSize="small" />} color={drawColor} />
          </IconButton>
          <IconButton
            className={cx(classes.toolbarButton, drawErase && 'active')}
            onClick={() => setDrawErase((v) => !v)}
            size="small"
            title="Eraser"
            aria-label="Eraser"
          >
            <EraserIcon fontSize="small" />
          </IconButton>
          <IconButton
            className={classes.toolbarButton}
            onClick={(e) => {
              // Seed the hex field from the current background; blank for the
              // transparent sentinel or a non-hex theme token.
              setBgHexDraft(drawBg === TRANSPARENT_BG ? '' : (normalizeBuiltinColor(drawBg) || ''));
              setBgColorAnchor(e.currentTarget);
            }}
            size="small"
            title="Canvas background"
            aria-label="Canvas background"
          >
            <ChipIcon icon={<FillIcon fontSize="small" />} color={drawBg} />
          </IconButton>
          <IconButton
            className={cx(classes.toolbarButton, classes.drawHistoryButton)}
            onClick={undoDrawing}
            size="small"
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            aria-label="Undo"
          >
            <UndoIcon fontSize="small" />
          </IconButton>
          <IconButton
            className={cx(classes.toolbarButton, classes.drawHistoryButton)}
            onClick={redoDrawing}
            size="small"
            disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z / Ctrl+Y)"
            aria-label="Redo"
          >
            <RedoIcon fontSize="small" />
          </IconButton>
          <div className={classes.toolbarDivider} />
          <Button size="small" onClick={clearDrawing} disabled={!canUndo && !canRedo}>
            Clear
          </Button>
          </>)}

          {/* Discord-style floating format toolbar over a mouse-selected range.
              Anchored to the selection-start point; centered above the text.
              disableAutoFocus/EnforceFocus so it doesn't steal focus from the
              textarea (which would collapse the native selection). */}
          <Popover
            open={Boolean(selectionPopover)}
            anchorReference="anchorPosition"
            anchorPosition={selectionPopover ? { top: selectionPopover.top, left: selectionPopover.left } : undefined}
            onClose={closeSelectionPopover}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            // Don't steal focus (keeps the textarea's native selection alive) and
            // don't block the page — dismissal is handled by our own document
            // mousedown listener, so the bar floats without a modal backdrop.
            disableAutoFocus
            disableEnforceFocus
            disableScrollLock
            hideBackdrop
            PaperProps={{ className: classes.selectionToolbar }}
          >
            <IconButton
              className={classes.toolbarButton}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyFormat('bold')}
              size="small"
              title="Bold"
              aria-label="Bold"
            >
              <BoldIcon fontSize="small" />
            </IconButton>
            <IconButton
              className={classes.toolbarButton}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyFormat('italic')}
              size="small"
              title="Italic"
              aria-label="Italic"
            >
              <ItalicIcon fontSize="small" />
            </IconButton>
            <IconButton
              className={classes.toolbarButton}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyFormat('underline')}
              size="small"
              title="Underline"
              aria-label="Underline"
            >
              <UnderlineIcon fontSize="small" />
            </IconButton>
          </Popover>

          {/* @-mention autocomplete is rendered by the editor's MentionPlugin
              (it portals the popover at the caret and owns keyboard nav). */}

          <Popover
            open={Boolean(emojiAnchor)}
            anchorEl={emojiAnchor}
            onClose={handleEmojiClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            // Let the picker's own surface show through (no Paper padding/bg).
            slotProps={{ paper: { className: classes.emojiPopoverPaper } }}
          >
            <EmojiPicker
              onEmojiClick={handleEmojiSelect}
              theme={EmojiTheme.DARK}
              lazyLoadEmojis
              autoFocusSearch={false}
              previewConfig={{ showPreview: false }}
              width={320}
              height={400}
            />
          </Popover>

          {/* Quick Replies — click a snippet to insert it into the composer;
              lightweight add/delete lives here, full management (edit /
              reorder / use counts) in the ProfileDialog pane. */}
          <Popover
            open={Boolean(quickReplyAnchor)}
            anchorEl={quickReplyAnchor}
            onClose={() => setQuickReplyAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{ paper: { className: classes.quickReplyPopoverPaper } }}
          >
            <QuickRepliesPopover
              quickReplies={quickReplies}
              onQuickRepliesChange={onQuickRepliesChange}
              onInsert={handleQuickReplyInsert}
            />
          </Popover>

          <Popover
            open={Boolean(colorAnchor)}
            anchorEl={colorAnchor}
            onClose={handleColorClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <div className={classes.colorPicker}>
              {COLOR_LIST.map((hex) => {
                const active = currentColor && currentColor.toLowerCase() === hex.toLowerCase();
                return (
                  <button
                    key={hex}
                    type="button"
                    className={cx(classes.colorSwatch, active && 'active')}
                    style={{ backgroundColor: hex }}
                    onClick={() => handleColorSelect(hex)}
                    aria-label={`Color ${hex}`}
                    title={hex}
                  />
                );
              })}
            </div>
          </Popover>

          {/* Brush width picker — a 1..MAX_BRUSH_SIZE integer slider with a live
              size preview above it (preview on top so a touch finger on the
              slider doesn't cover it) and an editable numeric value. The slider
              grabs focus on open so the scroll wheel adjusts the value. */}
          <Popover
            open={Boolean(widthAnchor)}
            anchorEl={widthAnchor}
            onClose={() => setWidthAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            TransitionProps={{
              // On open, focus the slider and attach a non-passive wheel handler
              // so the user can scroll to change the size right away (MUI's Slider
              // doesn't respond to the wheel on its own). Cleaned up on close.
              onEntered: () => {
                const root = brushSliderRef.current;
                if (!root) return;
                const input = root.querySelector('input');
                if (input) input.focus();
                const onWheel = (e) => {
                  e.preventDefault();
                  const delta = e.deltaY < 0 ? 1 : -1;
                  setDrawStroke((v) => Math.min(MAX_BRUSH_SIZE, Math.max(1, v + delta)));
                  setDrawErase(false);
                };
                root.addEventListener('wheel', onWheel, { passive: false });
                brushWheelCleanupRef.current = () => root.removeEventListener('wheel', onWheel);
              },
              onExited: () => {
                if (brushWheelCleanupRef.current) {
                  brushWheelCleanupRef.current();
                  brushWheelCleanupRef.current = null;
                }
              },
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 14, width: 240 }}>
              {/* Live preview of the brush size. Sized to fit the largest circle
                  (diameter = MAX_BRUSH_SIZE) so it never clips. */}
              <div style={{ height: MAX_BRUSH_SIZE + 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span
                  style={{
                    width: drawStroke,
                    height: drawStroke,
                    borderRadius: '50%',
                    background: drawErase ? 'rgba(255,255,255,0.25)' : drawColor,
                    display: 'block',
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                <Slider
                  ref={brushSliderRef}
                  value={drawStroke}
                  min={1}
                  max={MAX_BRUSH_SIZE}
                  step={1}
                  marks={false}
                  valueLabelDisplay="off"
                  onChange={(e, v) => { setDrawStroke(v); setDrawErase(false); }}
                  aria-label="Brush size"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: '#00d9ff',
                    '& .MuiSlider-rail': { opacity: 0.3, backgroundColor: '#fff' },
                  }}
                />
                <TextField
                  value={drawStroke}
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: MAX_BRUSH_SIZE, step: 1, 'aria-label': 'Brush size value', style: { width: 44, textAlign: 'center', padding: '6px 4px' } }}
                  sx={{ flex: '0 0 auto', width: 64 }}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value, 10);
                    if (Number.isNaN(raw)) return;
                    const v = Math.min(MAX_BRUSH_SIZE, Math.max(1, raw));
                    setDrawStroke(v);
                    setDrawErase(false);
                  }}
                />
              </div>
            </div>
          </Popover>

          {/* Brush color picker — the same palette + custom-hex entry as the
              avatar/username color picker (AvatarPickerDialog). */}
          <Popover
            open={Boolean(brushColorAnchor)}
            anchorEl={brushColorAnchor}
            onClose={() => setBrushColorAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <div className={classes.brushColorPicker}>
              <div className={classes.brushSwatchGrid}>
                {BRUSH_SWATCHES.map((hex) => {
                  const norm = normalizeBuiltinColor(hex);
                  const active =
                    !drawErase && normalizeBuiltinColor(drawColor) === norm;
                  return (
                    <button
                      key={hex}
                      type="button"
                      className={cx(classes.colorSwatch, active && 'active')}
                      style={{ backgroundColor: hex }}
                      onClick={() => { pickBrushColor(hex); setBrushColorAnchor(null); }}
                      aria-label={`Brush ${hex}`}
                      aria-pressed={active}
                      title={hex}
                    />
                  );
                })}
              </div>
              <div className={classes.brushHexRow}>
                <Typography className={classes.brushHexLabel}>Hex #</Typography>
                <TextField
                  className={classes.brushHexInput}
                  variant="outlined"
                  size="small"
                  value={brushHexDraft}
                  onChange={(e) => handleBrushHexInput(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: 6, spellCheck: false } }}
                />
              </div>
            </div>
          </Popover>

          {/* Canvas background picker — same palette + custom-hex entry as the
              avatar/brush picker, plus a transparent option (no fill baked into
              the exported PNG). */}
          <Popover
            open={Boolean(bgColorAnchor)}
            anchorEl={bgColorAnchor}
            onClose={() => setBgColorAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <div className={classes.brushColorPicker}>
              <div className={classes.brushSwatchGrid}>
                {/* Transparent option — checkerboard swatch; no fill baked in. */}
                <button
                  type="button"
                  className={cx(classes.colorSwatch, drawBg === TRANSPARENT_BG && 'active')}
                  style={CHECKERBOARD}
                  onClick={() => { setDrawBg(TRANSPARENT_BG); setDrawBgPicked(true); setBgHexDraft(''); setBgColorAnchor(null); }}
                  aria-label="Transparent background"
                  aria-pressed={drawBg === TRANSPARENT_BG}
                  title="Transparent"
                />
                {BRUSH_SWATCHES.map((hex) => {
                  const norm = normalizeBuiltinColor(hex);
                  const active =
                    drawBg !== TRANSPARENT_BG && normalizeBuiltinColor(drawBg) === norm;
                  return (
                    <button
                      key={hex}
                      type="button"
                      className={cx(classes.colorSwatch, active && 'active')}
                      style={{ backgroundColor: hex }}
                      onClick={() => { pickBgColor(hex); setBgColorAnchor(null); }}
                      aria-label={`Background ${hex}`}
                      aria-pressed={active}
                      title={hex}
                    />
                  );
                })}
              </div>
              <div className={classes.brushHexRow}>
                <Typography className={classes.brushHexLabel}>Hex #</Typography>
                <TextField
                  className={classes.brushHexInput}
                  variant="outlined"
                  size="small"
                  value={bgHexDraft}
                  onChange={(e) => handleBgHexInput(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: 6, spellCheck: false } }}
                />
              </div>
            </div>
          </Popover>
        </div>

        {drawingOpen && (
          // When the composer has been user-resized, this block fills the slack so
          // the canvas grows with the panel; otherwise it keeps its natural size.
          <div
            style={{
              marginBottom: 6,
              display: 'flex',
              flexDirection: 'column',
              ...(composerHeight != null ? { flex: 1, minHeight: 0 } : {}),
            }}
          >
            {/* The BOX carries the size; the canvas is absolutely positioned
                inside it so the backing store's intrinsic size never feeds back
                into layout. (A normal-flow canvas ratchets: the fit observer
                enlarges the backing store, whose intrinsic height then props the
                layout open — the panel could grow but never shrink, pushing the
                Cancel/Send row off-screen.) */}
            <div
              style={
                composerHeight != null
                  ? { position: 'relative', flex: 1, minHeight: 80 }
                  : { position: 'relative', height: DRAW_CANVAS_HEIGHT }
              }
            >
              <canvas
                ref={drawCanvasRef}
                tabIndex={0}
                onPointerDown={drawStart}
                onPointerMove={drawMove}
                onPointerUp={drawEnd}
                onPointerLeave={drawEnd}
                onKeyDown={handleDrawKeyDown}
                style={{
                  outline: 'none',
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  touchAction: 'none', cursor: 'crosshair', borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.15)',
                  ...(drawBg === TRANSPARENT_BG ? CHECKERBOARD : { background: drawBg }),
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
              <Button size="small" onClick={closeDrawing}>Cancel</Button>
              <div style={{ flex: 1 }} />
              <Button
                className={classes.sendButton}
                size="small"
                variant="contained"
                endIcon={uploadingImage ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                disabled={!isConnected || uploadingImage}
                onClick={sendDrawing}
              >
                Send drawing
              </Button>
            </div>
          </div>
        )}

        {convertingImage && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, opacity: 0.85 }}>
            <CircularProgress size={16} color="inherit" />
            <span style={{ fontSize: 13 }}>Converting image…</span>
          </div>
        )}
        {pendingImage && (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 6 }}>
            <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
              <img
                src={pendingImage.objectUrl}
                alt="Pending"
                style={{
                  maxWidth: IMAGE_THUMB_MAX_PX,
                  maxHeight: IMAGE_THUMB_MAX_PX,
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.2)',
                  display: 'block',
                }}
              />
              <button
                type="button"
                onClick={clearPendingImage}
                title="Remove image"
                style={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  fontSize: 12,
                  lineHeight: '18px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}
        {!drawingOpen && (
        <div className={classes.inputContainer}>
          <div className={classes.textInput}>
            <MessageEditor
              apiRef={editorApiRef}
              onTyping={handleEditorTyping}
              onSend={handleSendMessage}
              onImagePaste={stageImage}
              onSelectionRect={handleSelectionRect}
              onlineUsers={onlineUsers}
              currentUserId={currentUserId}
              quickReplies={quickReplies}
              placeholder={pendingImage ? 'Add a caption… (optional)' : 'Enter your text here, Shift+enter for multiline'}
              editorMaxHeight={composerHeight != null ? '100%' : undefined}
              classes={classes}
              cx={cx}
            />
            {imageDropError && (
              <div className={classes.editorDropError}>{imageDropError}</div>
            )}
          </div>
          <Button
            className={classes.sendButton}
            variant="contained"
            onClick={handleSendMessage}
            endIcon={uploadingImage ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            // Text may be sent while offline (socketService queues + flushes it
            // on reconnect), so we DON'T gate text on the connection. An image,
            // though, needs a live backend to upload — so when offline the
            // button is only enabled if there's text to queue.
            disabled={
              uploadingImage ||
              (!hasText && !pendingImage) ||
              (!isConnected && !hasText)
            }
          >
            Send
          </Button>
        </div>
        )}
      </div>

      {/* Full-screen photo lightbox — portaled to <body>, so it renders
          regardless of where in this tree it sits. Driven by which inline
          image thumbnail was clicked. */}
      <PhotoViewer
        open={!!viewerSrc}
        src={viewerSrc}
        alt="Shared image"
        onClose={() => setViewerSrc(null)}
      />
    </Paper>
  );
};

export default ChatPanel;